import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { isFast } from '@goaltree/backend/convex/lib/habitMeasure'
import { MUST_DO_LIMIT } from '@goaltree/backend/convex/lib/mustDo'
import { scheduleDays, scheduleFromDays } from '@goaltree/backend/convex/lib/schedule'
import { resolveTodaySectionOrder } from '@goaltree/backend/convex/lib/todaySections'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Calendar } from '@goaltree/ui/components/calendar'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@goaltree/ui/components/popover'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { enUS, es, fr, ja, pt, zhCN } from '@goaltree/ui/lib/locales'
import { cn } from '@goaltree/ui/lib/utils'
import { LegendList } from '@legendapp/list/react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import i18next from 'i18next'
import {
  BellIcon,
  CalendarDays,
  CheckIcon,
  ChevronRight as ChevronRightIcon,
  ClipboardList,
  Clock,
  Columns3,
  LayoutList,
  RefreshCw,
  Search as SearchIcon,
  Sprout,
  Sun,
  X,
  XIcon as XClose,
} from 'lucide-react'
import { motion } from 'motion/react'
import { usePostHog } from 'posthog-js/react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { create } from 'zustand'

import { AnimatedHeight } from '@/components/animated-height'
import { AppSidebar } from '@/components/app-sidebar'
import { BrainDumpEmptyAction, BrainDumpHeaderButton } from '@/components/brain-dump-entry'
import { CalendarSetupCard } from '@/components/calendar-setup-card'
import { CoachCard } from '@/components/coach-card'
import { CoachHeaderButton } from '@/components/coach-header-button'
import { CoachTonePrompt } from '@/components/coach-tone-prompt'
import { CoachEditedTag } from '@/components/coaching/coach-edited-tag'
import { CoachNoteCard } from '@/components/coaching/coach-notes'
import { ComingUpPanel } from '@/components/coming-up-panel'
import { DayShareDeepLink, ShareDayButton } from '@/components/day-share'
import { DensityToggle } from '@/components/density-toggle'
import { GettingStartedCard } from '@/components/getting-started-card'
import { ScrollFab } from '@/components/goal-tree-fab'
import { GroveInline } from '@/components/groves/grove-inline'
import { GroveToday } from '@/components/groves/grove-today'
import { GrovesList } from '@/components/groves/groves-list'
import { HabitIcon } from '@/components/habit-icon'
import { HabitAnchorLineById } from '@/components/habit-stack'
import { StrengthChip } from '@/components/habit-strength'
import { HabitSuggestionCard } from '@/components/habit-suggestion-card'
import { HabitTimePicker } from '@/components/habit-time-picker'
import { IconTip } from '@/components/icon-tip'
import { HabitDotGrid, habitColor, habitDayStreak, habitStats } from '@/components/inbox-habits'
import { InboxTaskDetailSheet, type InboxTaskForSheet } from '@/components/inbox-task-detail-sheet'
import { JourneyPromptCard } from '@/components/journey'
import { TodayLevelUp } from '@/components/level-up'
import { LinkifiedText } from '@/components/linkified-text'
import {
  SkeletonHabitRow,
  SkeletonProgressCard,
  SkeletonSectionLabel,
  SkeletonTaskRow,
} from '@/components/list-skeletons'
import { LiveStrip } from '@/components/live-strip'
import { LocationSetupCard } from '@/components/location-setup-card'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { MomentumFeed } from '@/components/momentum-feed'
import {
  MustDoHeader,
  MustDoNudge,
  MustDoRow,
  NUDGE_AFTER_DAYS,
  QueuedTray,
  SwipeActionSheet,
  nudgeDismissed,
} from '@/components/must-do'
import { useFastActions, useNumberHabitRow } from '@/components/number-habit'
import { OverdueSegment } from '@/components/overdue-segment'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { PromoBanner } from '@/components/promo-banner'
import { QuitChip } from '@/components/quit-habit'
import { RecursDaysChips } from '@/components/recurs-days-chips'
import { RotatingTip } from '@/components/rotating-tip'
import { StreakShare, WeeklyRecapCard } from '@/components/share-card'
import { SiteHeader } from '@/components/site-header'
import {
  HABIT_SKIP_REASONS,
  HabitSkipSheet,
  TASK_SKIP_REASONS,
  offerSkipReason,
} from '@/components/skip-reason-toast'
import { StoryRail } from '@/components/stories'
import { StreakFlame } from '@/components/streak-icons'
import { StreakStoryPrompt } from '@/components/streak-story-prompt'
import { TaskContextMenu, useLongPressMenu } from '@/components/task-context-menu'
import { TaskDetailSheet } from '@/components/task-detail-sheet'
import { LocationMeta } from '@/components/task-location'
import {
  CategoryMeta,
  FlameTip,
  HabitDoneAt,
  HabitFlameCheck,
  HabitRowSummary,
  HabitStatsLine,
  UrgentFlag,
  doneTodayAt,
  withPendingToday,
} from '@/components/task-meta'
import { TaskStatusIcon } from '@/components/task-status-icon'
import { TodayAllDone } from '@/components/today-all-done'
import { TodayHeaderMenu } from '@/components/today-header-menu'
import { TodayProgressCard, TodayProgressLine } from '@/components/today-progress-card'
import { TodaySchedule } from '@/components/today-schedule'
import { TodaySectionHeader } from '@/components/today-section-header'
import { TODAY_TAB_LABEL, type TodayTab, TodayTabs } from '@/components/today-tabs'
import { TrajectoryTeaser } from '@/components/trajectory-panel'
import { TrialChip } from '@/components/trial-chip'
import { YesterdayCatchUp } from '@/components/yesterday-catchup'
import { useBreather } from '@/hooks/use-breather'
import { useGoals } from '@/hooks/use-goals'
import { useMarkNotifsReadOnView } from '@/hooks/use-mark-notifs-read-on-view'
import {
  doneOnDay,
  dueOn,
  useMarkYesterday,
  yesterdayDate,
  yesterdayKey,
} from '@/hooks/use-mark-yesterday'
import { useIsMobile } from '@/hooks/use-mobile'
import { useMustDo } from '@/hooks/use-must-do'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { useHeaderSubtitle, useScrollTitle } from '@/hooks/use-scroll-title'
import { useSetTaskStatus } from '@/hooks/use-set-task-status'
import type { QueuedTask, TodayTask } from '@/hooks/use-today-tasks'
import { useTodayTasks } from '@/hooks/use-today-tasks'
import { useWebPush } from '@/hooks/use-web-push'
import { captureFieldShortcutLabel, isMacPlatform } from '@/lib/capture'
import { caretOffsetFromPoint } from '@/lib/caret'
import { useCategoryFill } from '@/lib/category-color'
import { celebrateFrom } from '@/lib/celebrate'
import { copyWithToast } from '@/lib/copy'
import { keyToDate, logicalDayStr, todayKey } from '@/lib/day'
import { clearStartHere, startHereGoalId } from '@/lib/first-run'
import { optimisticHabitCompletion } from '@/lib/habit-optimistic'
import { haptic } from '@/lib/haptics'
import {
  completeNode,
  optimisticDueDate,
  optimisticRename,
  removeNodes,
} from '@/lib/nodes-optimistic'
import { readSnapshot, writeSnapshot } from '@/lib/offline-cache'
import {
  installPlatform,
  noteWin,
  requestInstall,
  snoozeInstall,
  useCanAskInstall,
} from '@/lib/pwa'
import { habitDueOn, patchSchedule, periodLabel } from '@/lib/schedule'
import { playDoneSound } from '@/lib/sounds'
import { taskDescription, taskTags, taskTitle } from '@/lib/tags'
import { exactTaskMinutes, taskTimeLabel } from '@/lib/task-time'
import {
  coerceEnd,
  isDueTodayOrOverdue,
  isTaskOverdue,
  recurrenceOverdue,
  shiftTime,
  toMinutes,
} from '@/lib/time'
import { undoToast } from '@/lib/undo-toast'
import { useHeaderStore } from '@/store/header.store'
import { useOfflineStore } from '@/store/offline.store'
import { useViewStore } from '@/store/view.store'

import { GoalsEmptyState } from './_authenticated.goals.index'

export const Route = createFileRoute('/_authenticated/today')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: 'friends' | 'groves'; grove?: string; story?: string } => ({
    ...(search.tab === 'friends' || search.tab === 'groves' ? { tab: search.tab } : {}),
    ...(typeof search.grove === 'string' && search.grove ? { grove: search.grove } : {}),
    // A story notification: StoryRail opens that item.
    ...(typeof search.story === 'string' && search.story ? { story: search.story } : {}),
  }),
  head: () => ({
    meta: [{ title: 'Today — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

const DATE_FNS_LOCALES: Record<string, typeof enUS> = { en: enUS, es, fr, ja, pt, zh: zhCN }

const SWIPE_THRESHOLD = 80

// Skip the stagger entrance animation on subsequent visits — only play it once per session
let taskListHasEntered = false

// Remember today's skipped-habit ids across remounts AND page reloads (module +
// localStorage) so skipped habits aren't shown for a frame before
// getTodayHabitSkips reloads.
const HABIT_SKIPS_CACHE_KEY = 'today-habit-skips'
let lastHabitSkips: { date: string; ids: string[] } | null = null

// Cache habits across remounts (module + localStorage) so they hydrate instantly
// on revisit, matching how the task surface is cached — no habit pop-in.
const HABITS_CACHE_KEY = 'today-habits'
let lastKnownHabits: Habit[] | null = null

// Drives the habit dot-grid entrance animation on initial route load. The Today
// list mounts a habit row more than once as data streams in (habits query, then
// the daily surface), so we record the last animation time per habit and
// suppress replays that happen within a short window, letting the grid animate
// just once on appearance.
// One habit's full dot grid open on Today at a time.
const useTodayDots = create<{ open: string | null; toggle: (id: string) => void }>((set) => ({
  open: null,
  toggle: (id) => set((s) => ({ open: s.open === id ? null : id })),
}))

const lastDotAnim = new Map<string, number>()
function useHabitDotAnimate(id: string | undefined): boolean {
  const [animate] = useState(() => {
    if (!id) return false
    const now = Date.now()
    if (now - (lastDotAnim.get(id) ?? 0) < 1500) return false
    lastDotAnim.set(id, now)
    return true
  })
  return animate
}

function TaskCard({
  task,
  index,
  onComplete,
  onSkip,
  isDismissing,
  isSkipping,
  onPress,
  onInProgressSet: _onInProgressSet,
  focused,
  habitCompletions,
  startHere,
  flameTip,
  habitCreatedAt,
  divider = 'default',
}: {
  task: TodayTask & { localDone: boolean }
  index: number
  onComplete: (id: Id<'nodes'>) => void
  // picked = the reason was chosen up front (habit skip sheet), so no reason prompt after.
  onSkip?: (id: Id<'nodes'>, reason?: string, picked?: boolean) => void
  isDismissing: boolean
  isSkipping?: boolean
  onPress: (task: TodayTask & { localDone: boolean }) => void
  onInProgressSet?: () => void
  habitCompletions?: number[]
  likeCount?: number
  focused?: boolean
  startHere?: boolean
  flameTip?: boolean
  habitCreatedAt?: number
  // Inside the Must do card: a tinted line between rows, none under the last.
  divider?: 'default' | 'lane' | 'none'
}) {
  const { t } = useTranslation()
  const animateDots = useHabitDotAnimate(habitCompletions ? (task.taskId as string) : undefined)
  const dotsOpen = useTodayDots((s) => s.open === (task.taskId as string))
  const toggleDots = useTodayDots((s) => s.toggle)
  const mustDo = useMustDo()
  const markYesterday = useMarkYesterday()
  const [swipeSheet, setSwipeSheet] = useState(false)
  const [habitSkipSheet, setHabitSkipSheet] = useState(false)
  const renameNode = useMutation(api.nodes.rename).withOptimisticUpdate(optimisticRename)
  const updateRecursPeriod = useMutation(api.nodes.updateRecursPeriod).withOptimisticUpdate(
    (localStore, args) => {
      const habits = localStore.getQuery(api.nodes.getHabits, {})
      if (habits) {
        localStore.setQuery(
          api.nodes.getHabits,
          {},
          habits.map((h) =>
            h._id === args.taskId
              ? {
                  ...h,
                  ...patchSchedule(h, args),
                  recurrenceTime: args.recurrenceTime ?? h.recurrenceTime,
                }
              : h,
          ),
        )
      }
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (ctx) {
        localStore.setQuery(
          api.dailySurface.getTodayWithContext,
          { localDate },
          {
            ...ctx,
            tasks: ctx.tasks.map((t) =>
              t.taskId === args.taskId
                ? {
                    ...t,
                    ...patchSchedule(t, args),
                    recurrenceTime: args.recurrenceTime ?? t.recurrenceTime,
                  }
                : t,
            ),
          },
        )
      }
    },
  )
  const updateTaskDuration = useMutation(api.nodes.updateTaskDuration).withOptimisticUpdate(
    (localStore, args) => {
      const habits = localStore.getQuery(api.nodes.getHabits, {})
      if (habits) {
        localStore.setQuery(
          api.nodes.getHabits,
          {},
          habits.map((h) =>
            h._id === args.taskId ? { ...h, durationMinutes: args.durationMinutes } : h,
          ),
        )
      }
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (ctx) {
        localStore.setQuery(
          api.dailySurface.getTodayWithContext,
          { localDate },
          {
            ...ctx,
            tasks: ctx.tasks.map((t) =>
              t.taskId === args.taskId ? { ...t, durationMinutes: args.durationMinutes } : t,
            ),
          },
        )
      }
    },
  )
  const updateDueDate = useMutation(api.nodes.updateTaskDueDate).withOptimisticUpdate(
    optimisticDueDate,
  )
  const setTaskAllDay = useMutation(api.nodes.setTaskAllDay).withOptimisticUpdate(
    (localStore, args) => {
      // Habits render from getHabits; recurring tasks from the daily surface.
      const habits = localStore.getQuery(api.nodes.getHabits, {})
      if (habits) {
        localStore.setQuery(
          api.nodes.getHabits,
          {},
          habits.map((h) => (h._id === args.taskId ? { ...h, allDay: args.allDay } : h)),
        )
      }
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (ctx) {
        localStore.setQuery(
          api.dailySurface.getTodayWithContext,
          { localDate },
          {
            ...ctx,
            tasks: ctx.tasks.map((t) =>
              t.taskId === args.taskId ? { ...t, allDay: args.allDay } : t,
            ),
          },
        )
      }
    },
  )
  const setStatus = useMutation(api.nodes.setTaskStatus).withOptimisticUpdate(
    (localStore, args) => {
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (!ctx) return
      localStore.setQuery(
        api.dailySurface.getTodayWithContext,
        { localDate },
        {
          ...ctx,
          tasks: ctx.tasks.map((t) =>
            t.taskId === args.taskId ? { ...t, status: args.status } : t,
          ),
        },
      )
    },
  )
  const updateTaskEffort = useMutation(api.nodes.updateTaskEffort).withOptimisticUpdate(
    (localStore, args) => {
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (!ctx) return
      localStore.setQuery(
        api.dailySurface.getTodayWithContext,
        { localDate },
        {
          ...ctx,
          tasks: ctx.tasks.map((t) =>
            t.taskId === args.taskId
              ? { ...t, effort: args.effort, superUrgent: args.superUrgent || undefined }
              : t,
          ),
        },
      )
    },
  )
  const cardOnline = useOnlineStatus()
  const cardEnqueue = useOfflineStore((s) => s.enqueue)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressTriggered = useRef(false)
  function handleCheckboxPointerDown(e: React.PointerEvent) {
    if (isDone || locallyDone) return
    e.stopPropagation()
    longPressTriggered.current = false
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true
      const status = isInProgress ? 'backlog' : 'in-progress'
      if (cardOnline) setStatus({ taskId: task.taskId, status })
      else cardEnqueue({ kind: 'setTaskStatus', args: { taskId: task.taskId as string, status } })
    }, 500)
  }
  function handleCheckboxPointerUp() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }
  const [recursOpen, setRecursOpen] = useState(false)
  const [localRecursPeriod, setLocalRecursPeriod] = useState<string | null>(task.recursPeriod)
  const [localRecurrenceDay, setLocalRecurrenceDay] = useState<number>(task.recurrenceDay ?? 1)
  const [localDays, setLocalDays] = useState<number[]>(() =>
    scheduleDays(task.recursPeriod, task.recurrenceDay, task.recursDays),
  )
  const [daysOpen, setDaysOpen] = useState(false)
  const openDays = () => {
    setDaysOpen(true)
    setLocalDays(scheduleDays(localRecursPeriod, localRecurrenceDay, localDays))
  }
  const applyDays = (days: number[]) => {
    const sched = scheduleFromDays(days)
    setLocalDays(days)
    setLocalRecursPeriod(sched.recursPeriod)
    if (sched.recurrenceDay !== undefined) setLocalRecurrenceDay(sched.recurrenceDay)
    updateRecursPeriod({
      taskId: task.taskId,
      recursPeriod: 'days',
      recursDays: days,
      recurrenceTime: recurrenceTimeStr,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
  }
  // Switch a habit from all-day to timed, defaulting to the next full hour.
  const setHabitTime = (start: string, durationMinutes: number) => {
    setRecurrenceTimeStr(start)
    if (localRecursPeriod)
      updateRecursPeriod({
        taskId: task.taskId,
        recursPeriod: localRecursPeriod as
          | 'daily'
          | 'everyOtherDay'
          | 'weekly'
          | 'monthly'
          | 'weekend'
          | 'weekday'
          | 'days',
        recurrenceTime: start,
        recurrenceTzOffset: new Date().getTimezoneOffset(),
      }).catch(() => {})
    updateTaskDuration({ taskId: task.taskId, durationMinutes }).catch(() => {})
    if (task.allDay) setTaskAllDay({ taskId: task.taskId, allDay: false }).catch(() => {})
  }
  const [recurrenceTimeStr, setRecurrenceTimeStr] = useState(task.recurrenceTime ?? '09:00')
  const [dueDateOpen, setDueDateOpen] = useState(false)
  const [localDueDate, setLocalDueDate] = useState<number | undefined>(task.dueDate ?? undefined)
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(
    task.dueDate ? new Date(task.dueDate) : undefined,
  )
  const [timeStr, setTimeStr] = useState(() => {
    if (!task.dueDate) return '09:00'
    const d = new Date(task.dueDate)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  })
  const locale = DATE_FNS_LOCALES[i18next.language.split('-')[0]] ?? enUS

  function applyDueDate(d: Date) {
    setLocalDueDate(d.getTime())
    updateDueDate({ taskId: task.taskId, dueDate: d.getTime() }).catch(() => {})
    setDueDateOpen(false)
  }

  function handleDaySelect(day: Date | undefined) {
    if (!day) return
    setSelectedDay(day)
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    day.setHours(h, m, 0, 0)
    applyDueDate(day)
  }

  function handleDatePreset(preset: 'today' | 'tomorrow' | 'thisWeekend' | 'nextWeek') {
    const d = new Date()
    switch (preset) {
      case 'tomorrow':
        d.setDate(d.getDate() + 1)
        break
      case 'thisWeekend': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 6 ? 0 : 6 - day))
        break
      }
      case 'nextWeek': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 0 ? 1 : 8 - day))
        break
      }
    }
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    d.setHours(h, m, 0, 0)
    applyDueDate(d)
  }

  function handleTimeChange(val: string) {
    setTimeStr(val)
    if (!selectedDay) return
    const [h = 9, m = 0] = val.split(':').map(Number)
    const d = new Date(selectedDay)
    d.setHours(h, m, 0, 0)
    applyDueDate(d)
  }

  useEffect(() => {
    setLocalDueDate(task.dueDate ?? undefined)
    setSelectedDay(task.dueDate ? new Date(task.dueDate) : undefined)
    if (task.dueDate) {
      const d = new Date(task.dueDate)
      setTimeStr(
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      )
    }
  }, [task.dueDate])

  useEffect(() => {
    setLocalRecursPeriod(task.recursPeriod)
    setLocalRecurrenceDay(task.recurrenceDay ?? 1)
    setLocalDays(scheduleDays(task.recursPeriod, task.recurrenceDay, task.recursDays))
    setRecurrenceTimeStr(task.recurrenceTime ?? '09:00')
  }, [task.recursPeriod, task.recurrenceDay, task.recursDays, task.recurrenceTime])

  const [entered, setEntered] = useState(taskListHasEntered)
  const [swipeX, setSwipeX] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [skipping, setSkipping] = useState(false)
  const [tapReveal, setTapReveal] = useState(0)
  const [locallyDone, setLocallyDone] = useState(false)
  const habitDoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Habit rows check in with the flame instead of the circle; a second tap
  // during the hold cancels.
  const toggleHabitDone = () => {
    if (habitDoneTimer.current) {
      clearTimeout(habitDoneTimer.current)
      habitDoneTimer.current = null
      setLocallyDone(false)
      return
    }
    if (isDone || locallyDone) return
    setLocallyDone(true)
    habitDoneTimer.current = setTimeout(() => {
      habitDoneTimer.current = null
      setTapReveal(1)
      setCompleting(true)
      onComplete(task.taskId)
    }, 750)
  }
  const numberRow = useNumberHabitRow(task.taskId, 'mb-1.5')
  const cardRef = useRef<HTMLDivElement>(null)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(task.title)
  const renamingRef = useRef<HTMLTextAreaElement>(null)
  const originalTitleRef = useRef(task.title)
  const renameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tapPositionRef = useRef<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const el = renamingRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
    const pos = tapPositionRef.current
    if (pos) {
      tapPositionRef.current = null
      const offset = caretOffsetFromPoint(el, pos.x, pos.y)
      el.setSelectionRange(offset, offset)
    }
  }, [renaming])
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  // Axis lock: once a drag is decided horizontal or vertical, stick to it so a
  // vertical scroll never turns into a swipe.
  const swipeAxis = useRef<'none' | 'h' | 'v'>('none')
  const isDone = task.localDone || task.completedAt !== null
  const isInProgress = task.status === 'in-progress' && !isDone
  const dotCompletions = habitCompletions && withPendingToday(habitCompletions, locallyDone)
  const habitDoneAt = habitCompletions && doneTodayAt(habitCompletions)
  const displayTitle = task.isInbox ? taskTitle(task) : task.title
  const parsedTags = task.isInbox ? taskTags(task) : []
  const parsedDesc = task.isInbox ? taskDescription(task) : null
  const ctxMenu = useLongPressMenu({})
  const [pendingDelete, setPendingDelete] = useState(false)
  const removeNode = useMutation(api.nodes.remove).withOptimisticUpdate((localStore, { nodeId }) =>
    removeNodes(localStore, [nodeId]),
  )
  const setRowStatus = (status: 'backlog' | 'in-progress') => {
    if (cardOnline) setStatus({ taskId: task.taskId, status })
    else cardEnqueue({ kind: 'setTaskStatus', args: { taskId: task.taskId as string, status } })
  }
  // Today is a one-way surface: completing dismisses the card (with an undo
  // toast), so the menu only ever acts on live cards — no uncomplete branch.
  // onComplete routes to the habit-aware handler for habit rows.
  const menuInProgress = () => setRowStatus(isInProgress ? 'backlog' : 'in-progress')
  const menuComplete = () => {
    if (!isDone) onComplete(task.taskId)
  }
  const menuSkip = () => {
    if (habitCompletions) {
      setHabitSkipSheet(true)
      return
    }
    setSkipping(true)
    onSkip?.(task.taskId)
  }
  const menuUrgent = () =>
    updateTaskEffort({
      taskId: task.taskId,
      effort: task.effort === 'high' ? 'low' : 'high',
    }).catch(() => {})
  const menuSuperUrgent = () =>
    updateTaskEffort({
      taskId: task.taskId,
      effort: 'high',
      superUrgent: !task.superUrgent,
    }).catch(() => {})

  useEffect(() => {
    if (taskListHasEntered) return
    const t = setTimeout(() => {
      setEntered(true)
      taskListHasEntered = true
    }, index * 60)
    return () => clearTimeout(t)
  }, [])

  function handleTouchStart(e: React.TouchEvent) {
    if (isDone) return
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    swipeAxis.current = 'none'
    setIsSwiping(true)
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (touchStartX.current === null) return
    const dx = e.touches[0].clientX - touchStartX.current
    const dy = e.touches[0].clientY - (touchStartY.current ?? 0)
    // Lock the axis on the first meaningful movement; ignore swipes on a
    // vertical (scroll) gesture.
    if (swipeAxis.current === 'none') {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      swipeAxis.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
    }
    if (swipeAxis.current === 'v') return
    // Right → complete; left → skip ("not today"). Left only when skip is wired.
    if (dx > 0 || onSkip) setSwipeX(dx)
  }

  function handleTouchEnd() {
    if (swipeX >= SWIPE_THRESHOLD) {
      celebrateFrom(cardRef.current)
      setCompleting(true)
      setIsSwiping(false)
      onComplete(task.taskId)
      touchStartX.current = null
      return
    }
    if (!habitCompletions && swipeX <= -SWIPE_THRESHOLD) {
      setSwipeX(0)
      setIsSwiping(false)
      touchStartX.current = null
      setSwipeSheet(true)
      return
    }
    if (onSkip && habitCompletions && swipeX <= -SWIPE_THRESHOLD) {
      setSwipeX(0)
      setIsSwiping(false)
      touchStartX.current = null
      setHabitSkipSheet(true)
      return
    }
    if (onSkip && swipeX <= -SWIPE_THRESHOLD) {
      setSkipping(true)
      setIsSwiping(false)
      onSkip(task.taskId)
      touchStartX.current = null
      return
    }
    setSwipeX(0)
    setIsSwiping(false)
    touchStartX.current = null
  }

  const swipeProgress = Math.min(Math.max(swipeX, 0) / SWIPE_THRESHOLD, 1)
  const skipProgress = Math.min(Math.max(-swipeX, 0) / SWIPE_THRESHOLD, 1)

  const opacity = isDismissing ? 0 : !entered ? 0 : isDone ? 0.6 : 1
  const transform =
    skipping || isSkipping
      ? 'translateX(-110%)'
      : isDismissing || completing
        ? 'translateX(110%)'
        : entered
          ? `translate(${swipeX}px, 0)`
          : 'translateY(14px)'

  return (
    <>
      {ctxMenu.menu && (
        <TaskContextMenu
          pos={ctxMenu.menu}
          onClose={ctxMenu.closeMenu}
          title={task.title}
          canPin={false}
          isDone={isDone}
          isInProgress={isInProgress}
          isUrgent={task.effort === 'high'}
          isSuperUrgent={!!task.superUrgent}
          onSuperUrgent={task.isInbox ? menuSuperUrgent : undefined}
          isPinned={false}
          onInProgress={menuInProgress}
          onPin={() => {}}
          onComplete={menuComplete}
          onSkip={habitCompletions && onSkip ? menuSkip : undefined}
          onUrgent={menuUrgent}
          onDelete={() => setPendingDelete(true)}
          mustDo={
            habitCompletions
              ? undefined
              : { committed: task.mustDo, onCommit: (day) => void mustDo(task.taskId, day) }
          }
          yesterday={
            habitCompletions && !isFast(numberRow.progress?.measure)
              ? {
                  done: doneOnDay(habitCompletions, yesterdayKey()),
                  onToggle: (done) => void markYesterday(task.taskId, task.title, done),
                }
              : undefined
          }
        />
      )}
      <AlertDialog open={pendingDelete} onOpenChange={setPendingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.deleteTaskTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common.deleteTaskDesc', { title: task.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                haptic('warning')
                removeNode({ nodeId: task.taskId }).catch(() => toast.error("Couldn't delete"))
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {habitCompletions && onSkip && (
        <HabitSkipSheet
          open={habitSkipSheet}
          onOpenChange={setHabitSkipSheet}
          title={task.title}
          onSkip={(reason) => {
            setSkipping(true)
            onSkip(task.taskId, reason, true)
          }}
        />
      )}
      {!habitCompletions && (
        <SwipeActionSheet
          open={swipeSheet}
          onOpenChange={setSwipeSheet}
          title={task.title}
          committed={task.mustDo}
          onCommit={(day) => void mustDo(task.taskId, day)}
          onSkip={onSkip ? menuSkip : undefined}
        />
      )}
      {/* Outer wrapper collapses height + margin so the cards below smoothly fill the gap */}
      <div
        style={{
          maxHeight: isDismissing ? 0 : dotsOpen ? 'none' : (cardRef.current?.offsetHeight ?? 200),
          marginBottom: 0,
          overflow: 'hidden',
          transition: 'max-height 300ms ease',
          position: 'relative',
        }}
      >
        {/* Green reveal layer behind the card */}
        <div
          aria-hidden
          style={{
            opacity: Math.max(swipeProgress, tapReveal),
            transition: isSwiping ? 'none' : 'opacity 200ms ease-out',
          }}
          className="pointer-events-none absolute inset-0 flex items-start bg-primary pt-3 pl-4"
        >
          <CheckIcon
            className="h-5 w-5 text-white dark:text-[#0a0a0a]"
            strokeWidth={3}
            style={{
              transform: `scale(${Math.max(swipeProgress, tapReveal) * 0.65 + 0.35})`,
              transition: isSwiping
                ? 'none'
                : 'transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            }}
          />
        </div>

        {/* "Not today" reveal behind the card when swiping left to skip */}
        {onSkip && (
          <div
            aria-hidden
            style={{
              opacity: skipProgress,
              transition: isSwiping ? 'none' : 'opacity 200ms ease-out',
            }}
            className="pointer-events-none absolute inset-0 flex items-start justify-end bg-muted pt-3 pr-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          >
            <span
              style={{
                transform: `scale(${skipProgress * 0.35 + 0.65})`,
                transition: isSwiping
                  ? 'none'
                  : 'transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              }}
            >
              {t('today.skipNotToday')}
            </span>
          </div>
        )}

        <div
          ref={cardRef}
          data-today-task-id={task.taskId as string}
          data-swipe-x={isDone ? undefined : ''}
          onClick={(e) => {
            if (ctxMenu.suppressClick(e)) return
            onPress(task)
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          {...ctxMenu.longPressHandlers}
          style={{
            transition: isSwiping
              ? 'opacity 300ms ease'
              : 'transform 300ms ease, opacity 300ms ease',
            transform,
            opacity,
          }}
          className={cn(
            'relative cursor-pointer select-none [-webkit-touch-callout:none] bg-transparent px-1 py-3 transition-colors hover:bg-muted/40',
            divider === 'default' && 'border-b border-border/60',
            divider === 'lane' && 'border-b border-primary/15',
            (focused || startHere) && 'bg-primary/5',
          )}
        >
          {/* Row 1: status + title */}
          <div className="flex items-start gap-2.5">
            {habitCompletions ? (
              <HabitFlameCheck
                done={isDone || locallyDone}
                onToggle={numberRow.progress ? numberRow.add : toggleHabitDone}
                inProgress={isInProgress}
              />
            ) : (
              <button
                type="button"
                onPointerDown={handleCheckboxPointerDown}
                onPointerUp={handleCheckboxPointerUp}
                onPointerLeave={handleCheckboxPointerUp}
                onPointerCancel={handleCheckboxPointerUp}
                onClick={(e) => {
                  e.stopPropagation()
                  if (longPressTriggered.current) {
                    longPressTriggered.current = false
                    return
                  }
                  if (!isDone && !locallyDone) {
                    celebrateFrom(e.currentTarget)
                    setLocallyDone(true)
                    setTimeout(() => {
                      setTapReveal(1)
                      setCompleting(true)
                      onComplete(task.taskId)
                    }, 750)
                  }
                }}
                disabled={isDone}
                aria-label={
                  isDone ? 'Task complete' : isInProgress ? 'In progress' : t('inbox.markDone')
                }
                title={isInProgress ? t('goals.inProgress') : `${t('inbox.markDone')} (Space)`}
                className="-my-1.5 -mr-1.5 -ml-1 shrink-0 cursor-pointer rounded-full p-1.5 text-foreground outline-none transition-colors hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60"
              >
                <TaskStatusIcon
                  status={isDone || locallyDone ? 'done' : isInProgress ? 'in_progress' : 'pending'}
                  size={20}
                />
              </button>
            )}
            {renaming ? (
              <textarea
                ref={renamingRef}
                autoFocus
                rows={1}
                value={renameValue}
                onChange={(e) => {
                  const val = e.target.value
                  setRenameValue(val)
                  e.target.style.height = 'auto'
                  e.target.style.height = `${e.target.scrollHeight}px`
                  if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current)
                  renameDebounceRef.current = setTimeout(() => {
                    const trimmed = val.trim()
                    if (trimmed)
                      renameNode({ nodeId: task.taskId, title: trimmed }).catch(() =>
                        toast.error("Couldn't rename"),
                      )
                  }, 400)
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current)
                    const trimmed = renameValue.trim()
                    if (trimmed)
                      renameNode({ nodeId: task.taskId, title: trimmed }).catch(() =>
                        toast.error("Couldn't rename"),
                      )
                    setRenaming(false)
                  }
                  if (e.key === 'Escape') {
                    if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current)
                    const original = originalTitleRef.current
                    setRenameValue(original)
                    setRenaming(false)
                    renameNode({ nodeId: task.taskId, title: original }).catch(() => {})
                  }
                }}
                onBlur={() => {
                  if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current)
                  const trimmed = renameValue.trim()
                  if (trimmed)
                    renameNode({ nodeId: task.taskId, title: trimmed }).catch(() =>
                      toast.error("Couldn't rename"),
                    )
                  setRenaming(false)
                }}
                className="flex-1 resize-none overflow-hidden bg-transparent text-[15px] font-semibold leading-snug text-foreground outline-none"
              />
            ) : (
              <>
                {habitCompletions && <HabitIcon habit={task} />}
                <p
                  className={cn(
                    'flex-1 text-[15px] font-semibold leading-snug',
                    isDone ? 'text-muted-foreground/60 line-through' : 'text-foreground',
                  )}
                >
                  <span
                    onClick={(e) => {
                      if (window.matchMedia('(pointer: coarse)').matches) return
                      e.stopPropagation()
                      tapPositionRef.current = { x: e.clientX, y: e.clientY }
                      originalTitleRef.current = task.title
                      setRenameValue(task.title)
                      setRenaming(true)
                    }}
                    className="cursor-text hover:underline decoration-dashed underline-offset-2"
                  >
                    {displayTitle}
                  </span>
                </p>
              </>
            )}
            {task.isInbox && task.effort === 'high' && !isDone && (
              <UrgentFlag
                title={task.superUrgent ? undefined : t('filter.priorityUrgent')}
                superUrgent={task.superUrgent}
                className="mt-[3px]"
              />
            )}
            {startHere && !isDone && (
              <span className="mt-[1px] shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
                {t('today.startHere')}
              </span>
            )}
          </div>

          {habitCompletions && (
            <HabitAnchorLineById
              id={task.taskId}
              className={cn('mt-0.5 ml-[30px]', isDone && 'opacity-30')}
            />
          )}

          {task.lastEditedBy && (
            <CoachEditedTag
              by={task.lastEditedBy}
              className={cn('mt-0.5 ml-[30px] flex', isDone && 'opacity-30')}
            />
          )}

          {/* Row 1: description (inbox only) */}
          {task.isInbox && parsedDesc && (
            <p
              className={cn(
                'mt-0.5 ml-[30px] line-clamp-2 text-[13px] text-muted-foreground',
                isDone && 'opacity-30',
              )}
            >
              <LinkifiedText text={parsedDesc} />
            </p>
          )}

          {/* Row 2: context line */}
          <div className={cn('mt-1 ml-[30px] min-w-0', isDone && 'opacity-30')}>
            {task.isInbox ? (
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[11px] text-muted-foreground">
                {habitCompletions && task.category && task.category !== 'inbox' && (
                  <CategoryMeta category={task.category} />
                )}
                {(task.recurs || task.recursPeriod) && (
                  <span className={cn('flex items-center gap-0.5 leading-none')}>
                    <Popover
                      open={recursOpen}
                      onOpenChange={(o) => {
                        setRecursOpen(o)
                        if (!o) setDaysOpen(false)
                      }}
                    >
                      <PopoverTrigger
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 rounded px-1 py-0.5 cursor-pointer hover:bg-muted transition-colors"
                      >
                        <RefreshCw className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
                        {periodLabel(t, i18next.language, localRecursPeriod, localDays)}
                        {localRecursPeriod === 'weekly' && (
                          <span className="text-current/70">
                            {' · '}
                            {new Intl.DateTimeFormat(i18next.language, { weekday: 'short' }).format(
                              new Date(2025, 0, 5 + localRecurrenceDay),
                            )}
                          </span>
                        )}
                        {localRecursPeriod === 'monthly' && (
                          <span className="text-current/70">
                            {' · '}
                            {(() => {
                              if (!i18next.language.startsWith('en')) return localRecurrenceDay
                              try {
                                const r = new Intl.PluralRules(i18next.language, {
                                  type: 'ordinal',
                                }).select(localRecurrenceDay)
                                const s: Record<string, string> = {
                                  one: 'st',
                                  two: 'nd',
                                  few: 'rd',
                                  other: 'th',
                                }
                                return `${localRecurrenceDay}${s[r] ?? 'th'}`
                              } catch {
                                return localRecurrenceDay
                              }
                            })()}
                          </span>
                        )}
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto min-w-[140px] p-1"
                        side="bottom"
                        align="start"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {(
                          [
                            'daily',
                            'everyOtherDay',
                            'weekly',
                            'monthly',
                            'weekend',
                            'weekday',
                          ] as const
                        ).map((p) => {
                          const needsDay = p === 'weekly' || p === 'monthly'
                          if (needsDay) {
                            return (
                              <button
                                key={p}
                                type="button"
                                className="w-full"
                                onClick={() => {
                                  setLocalRecursPeriod(p)
                                  updateRecursPeriod({
                                    taskId: task.taskId,
                                    recursPeriod: p,
                                    recurrenceTime: recurrenceTimeStr,
                                    recurrenceTzOffset: new Date().getTimezoneOffset(),
                                    recurrenceDay: localRecurrenceDay,
                                  }).catch(() => {})
                                }}
                              >
                                <span
                                  className={cn(
                                    'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
                                    localRecursPeriod === p
                                      ? 'text-primary-text'
                                      : 'text-foreground',
                                  )}
                                >
                                  <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
                                  {t(`inbox.period.${p}`)}
                                </span>
                              </button>
                            )
                          }
                          return (
                            <PopoverClose
                              key={p}
                              className="w-full"
                              onClick={() => {
                                setLocalRecursPeriod(p)
                                updateRecursPeriod({
                                  taskId: task.taskId,
                                  recursPeriod: p,
                                  recurrenceTime: recurrenceTimeStr,
                                  recurrenceTzOffset: new Date().getTimezoneOffset(),
                                }).catch(() => {})
                              }}
                            >
                              <span
                                className={cn(
                                  'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
                                  localRecursPeriod === p ? 'text-primary-text' : 'text-foreground',
                                )}
                              >
                                <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
                                {t(`inbox.period.${p}`)}
                              </span>
                            </PopoverClose>
                          )
                        })}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            openDays()
                          }}
                          className={cn(
                            'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
                            daysOpen || localRecursPeriod === 'days'
                              ? 'text-primary-text'
                              : 'text-foreground',
                          )}
                        >
                          <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
                          {t('inbox.period.days')}
                        </button>
                        {(daysOpen || localRecursPeriod === 'days') && (
                          <RecursDaysChips
                            className="mt-1 border-t border-border/40 px-2 py-2"
                            days={localDays}
                            onChange={applyDays}
                          />
                        )}
                        {localRecursPeriod === 'weekly' && !daysOpen && (
                          <div className="flex gap-0.5 px-2 py-2 border-t border-border/40 mt-1">
                            {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                              <PopoverClose
                                key={d}
                                className="flex h-7 w-7 items-center justify-center rounded cursor-pointer"
                                onClick={() => {
                                  setLocalRecurrenceDay(d)
                                  updateRecursPeriod({
                                    taskId: task.taskId,
                                    recursPeriod: 'weekly',
                                    recurrenceDay: d,
                                    recurrenceTime: recurrenceTimeStr,
                                    recurrenceTzOffset: new Date().getTimezoneOffset(),
                                  }).catch(() => {})
                                }}
                              >
                                <span
                                  className={cn(
                                    'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors hover:bg-muted',
                                    localRecurrenceDay === d
                                      ? 'bg-primary text-primary-foreground'
                                      : 'text-foreground',
                                  )}
                                >
                                  {new Intl.DateTimeFormat(i18next.language, {
                                    weekday: 'narrow',
                                  }).format(new Date(2025, 0, 5 + d))}
                                </span>
                              </PopoverClose>
                            ))}
                          </div>
                        )}
                        {localRecursPeriod === 'monthly' && (
                          <div className="grid grid-cols-7 gap-0.5 px-2 py-2 border-t border-border/40 mt-1 w-[200px]">
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                              <PopoverClose
                                key={d}
                                className="flex h-7 w-7 items-center justify-center rounded cursor-pointer"
                                onClick={() => {
                                  setLocalRecurrenceDay(d)
                                  updateRecursPeriod({
                                    taskId: task.taskId,
                                    recursPeriod: 'monthly',
                                    recurrenceDay: d,
                                    recurrenceTime: recurrenceTimeStr,
                                    recurrenceTzOffset: new Date().getTimezoneOffset(),
                                  }).catch(() => {})
                                }}
                              >
                                <span
                                  className={cn(
                                    'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors hover:bg-muted',
                                    localRecurrenceDay === d
                                      ? 'bg-primary text-primary-foreground'
                                      : 'text-foreground',
                                  )}
                                >
                                  {d}
                                </span>
                              </PopoverClose>
                            ))}
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                    <HabitTimePicker
                      className="ml-1"
                      allDay={!!task.allDay}
                      start={recurrenceTimeStr}
                      durationMinutes={task.durationMinutes ?? 60}
                      overdue={
                        !isDone &&
                        !locallyDone &&
                        !task.allDay &&
                        recurrenceOverdue(task.recurrenceTime ?? '09:00', task.durationMinutes)
                      }
                      onSet={setHabitTime}
                      onAllDay={() =>
                        setTaskAllDay({ taskId: task.taskId, allDay: true }).catch(() => {})
                      }
                    />
                  </span>
                )}
                {localDueDate && !task.recurs && !task.recursPeriod && (
                  <span
                    className={cn(
                      'flex items-center gap-0.5 leading-none',
                      isDueTodayOrOverdue(localDueDate) ? 'text-red-600 dark:text-red-400' : '',
                    )}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {isTaskOverdue(localDueDate, task.allDay) && <OverdueSegment />}
                    <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                      <PopoverTrigger
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className={cn(
                          'flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 transition-colors',
                          isDueTodayOrOverdue(localDueDate)
                            ? 'hover:bg-red-500/10'
                            : 'hover:bg-muted',
                        )}
                      >
                        <CalendarDays className="h-2.5 w-2.5 shrink-0" />
                        {(() => {
                          const d = new Date(localDueDate)
                          const dateStr = d.toLocaleDateString(i18next.language, {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                          })
                          if (task.allDay) return dateStr
                          const start = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
                          if ((task.durationMinutes ?? 0) > 60) {
                            const e2 = new Date(localDueDate + (task.durationMinutes ?? 0) * 60_000)
                            const end = `${String(e2.getHours()).padStart(2, '0')}:${String(e2.getMinutes()).padStart(2, '0')}`
                            return `${dateStr}, ${start} – ${end}`
                          }
                          return `${dateStr}, ${start}`
                        })()}
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[min(20rem,92vw)] p-0"
                        side="bottom"
                        align="start"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex w-full flex-wrap justify-center gap-1 px-3 pt-2 pb-0">
                          {(['today', 'tomorrow', 'thisWeekend', 'nextWeek'] as const).map((p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => handleDatePreset(p)}
                              className="cursor-pointer rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                            >
                              {t(`inbox.datePreset.${p}`)}
                            </button>
                          ))}
                        </div>
                        <Calendar
                          mode="single"
                          disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                          selected={selectedDay}
                          defaultMonth={selectedDay ?? new Date()}
                          onSelect={handleDaySelect}
                          locale={locale}
                          classNames={{
                            root: 'w-full',
                            range_start: '',
                            range_middle: '',
                            range_end: '',
                            today: 'rounded-(--cell-radius) bg-muted text-foreground',
                          }}
                        />
                        <div className="flex w-full items-center justify-center gap-2 border-t border-border px-3 pt-3 pb-1">
                          <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <div
                            className={cn('flex items-center gap-1.5', task.allDay && 'opacity-40')}
                          >
                            <input
                              type="time"
                              lang="en-GB"
                              value={timeStr}
                              onChange={(e) => handleTimeChange(e.target.value)}
                              className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
                            />
                            <span className="text-muted-foreground/40 text-xs select-none">→</span>
                            <input
                              type="time"
                              lang="en-GB"
                              value={shiftTime(timeStr, task.durationMinutes ?? 60)}
                              onChange={(e) => {
                                const newEnd = coerceEnd(timeStr, e.target.value)
                                const dur = Math.max(15, toMinutes(newEnd) - toMinutes(timeStr))
                                updateTaskDuration({
                                  taskId: task.taskId,
                                  durationMinutes: dur,
                                }).catch(() => {})
                              }}
                              className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
                            />
                          </div>
                        </div>
                        <div className="w-full px-3 pb-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setTaskAllDay({ taskId: task.taskId, allDay: !task.allDay }).catch(
                                () => {},
                              )
                            }}
                            className={cn(
                              'flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
                              task.allDay
                                ? 'border-primary/40 bg-primary/10 text-primary-text hover:bg-primary/15'
                                : 'border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground',
                            )}
                          >
                            <Sun className="h-3 w-3 shrink-0" />
                            {t('inbox.allDay')}
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setLocalDueDate(undefined)
                        setSelectedDay(undefined)
                        updateDueDate({ taskId: task.taskId, dueDate: undefined }).catch(() => {})
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className={cn(
                        'flex cursor-pointer items-center rounded px-0.5 py-0.5 text-muted-foreground/60 transition-colors hover:text-foreground',
                        isDueTodayOrOverdue(localDueDate)
                          ? 'hover:bg-red-500/10'
                          : 'hover:bg-muted',
                      )}
                    >
                      <X className="h-2.5 w-2.5" strokeWidth={2.5} />
                    </button>
                  </span>
                )}
                {task.location && <LocationMeta location={task.location} />}
                {parsedTags.map((tag) => (
                  <span key={tag} className="leading-none">
                    #{tag}
                  </span>
                ))}
                {habitDoneAt !== undefined && <HabitDoneAt at={habitDoneAt} />}
                {habitCompletions && <QuitChip habitId={task.taskId} />}
              </div>
            ) : task.category || task.goalTitle ? (
              <div className="flex items-center gap-2.5 overflow-hidden font-mono text-[11px] text-muted-foreground">
                {task.category && <CategoryMeta category={task.category} />}
                {habitCompletions && <QuitChip habitId={task.taskId} />}
                {task.goalTitle && <p className="min-w-0 truncate">{task.goalTitle}</p>}
              </div>
            ) : null}
          </div>

          {/* Row 3: goal tasks only — effort · recurrence · due date */}
          {!task.isInbox && (
            <div className={cn('mt-1 ml-[30px] flex items-center', isDone && 'opacity-30')}>
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[11px] text-muted-foreground">
                <span className="order-last shrink-0">
                  {exactTaskMinutes(task)
                    ? taskTimeLabel(task)
                    : `${taskTimeLabel(task)} · ${t(`stats.effort${task.effort.charAt(0).toUpperCase() + task.effort.slice(1)}`)}`}
                </span>
                {(task.recurs || localRecursPeriod) && (
                  <span className="flex items-center leading-none">
                    <Popover
                      open={recursOpen}
                      onOpenChange={(o) => {
                        setRecursOpen(o)
                        if (!o) setDaysOpen(false)
                      }}
                    >
                      <PopoverTrigger
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 rounded px-1 py-0.5 cursor-pointer hover:bg-muted transition-colors"
                      >
                        <RefreshCw className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
                        {periodLabel(t, i18next.language, localRecursPeriod, localDays)}
                        {localRecursPeriod === 'weekly' && (
                          <span className="text-current/70">
                            {' · '}
                            {new Intl.DateTimeFormat(i18next.language, { weekday: 'short' }).format(
                              new Date(2025, 0, 5 + localRecurrenceDay),
                            )}
                          </span>
                        )}
                        {localRecursPeriod === 'monthly' && (
                          <span className="text-current/70">
                            {' · '}
                            {(() => {
                              if (!i18next.language.startsWith('en')) return localRecurrenceDay
                              try {
                                const r = new Intl.PluralRules(i18next.language, {
                                  type: 'ordinal',
                                }).select(localRecurrenceDay)
                                const s: Record<string, string> = {
                                  one: 'st',
                                  two: 'nd',
                                  few: 'rd',
                                  other: 'th',
                                }
                                return `${localRecurrenceDay}${s[r] ?? 'th'}`
                              } catch {
                                return localRecurrenceDay
                              }
                            })()}
                          </span>
                        )}
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto min-w-[140px] p-1"
                        side="bottom"
                        align="start"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {(
                          [
                            'daily',
                            'everyOtherDay',
                            'weekly',
                            'monthly',
                            'weekend',
                            'weekday',
                          ] as const
                        ).map((p) => {
                          const needsDay = p === 'weekly' || p === 'monthly'
                          if (needsDay) {
                            return (
                              <button
                                key={p}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setLocalRecursPeriod(p)
                                }}
                                className={cn(
                                  'w-full cursor-pointer rounded px-2 py-1 text-left text-xs transition-colors hover:bg-muted',
                                  localRecursPeriod === p ? 'text-primary-text' : 'text-foreground',
                                )}
                              >
                                {t(`inbox.period.${p}`)}
                              </button>
                            )
                          }
                          return (
                            <PopoverClose
                              key={p}
                              onClick={(e) => {
                                e.stopPropagation()
                                setLocalRecursPeriod(p)
                                updateRecursPeriod({
                                  taskId: task.taskId,
                                  recursPeriod: p,
                                  recurrenceTime: recurrenceTimeStr,
                                  recurrenceTzOffset: new Date().getTimezoneOffset(),
                                }).catch(() => {})
                              }}
                              className={cn(
                                'w-full cursor-pointer rounded px-2 py-1 text-left text-xs transition-colors hover:bg-muted',
                                localRecursPeriod === p ? 'text-primary-text' : 'text-foreground',
                              )}
                            >
                              {t(`inbox.period.${p}`)}
                            </PopoverClose>
                          )
                        })}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            openDays()
                          }}
                          className={cn(
                            'w-full cursor-pointer rounded px-2 py-1 text-left text-xs transition-colors hover:bg-muted',
                            daysOpen || localRecursPeriod === 'days'
                              ? 'text-primary-text'
                              : 'text-foreground',
                          )}
                        >
                          {t('inbox.period.days')}
                        </button>
                        {(daysOpen || localRecursPeriod === 'days') && (
                          <RecursDaysChips
                            className="mt-1 border-t border-border/40 px-2 py-2"
                            days={localDays}
                            onChange={applyDays}
                          />
                        )}
                        {localRecursPeriod === 'weekly' && !daysOpen && (
                          <div className="mt-1 grid grid-cols-4 gap-0.5 border-t border-border pt-1">
                            {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                              <PopoverClose
                                key={d}
                                className="flex h-7 w-7 items-center justify-center rounded cursor-pointer"
                                onClick={() => {
                                  setLocalRecurrenceDay(d)
                                  updateRecursPeriod({
                                    taskId: task.taskId,
                                    recursPeriod: 'weekly',
                                    recurrenceDay: d,
                                    recurrenceTime: recurrenceTimeStr,
                                    recurrenceTzOffset: new Date().getTimezoneOffset(),
                                  }).catch(() => {})
                                }}
                              >
                                <span
                                  className={cn(
                                    'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors hover:bg-muted',
                                    localRecurrenceDay === d
                                      ? 'bg-primary text-primary-foreground'
                                      : 'text-foreground',
                                  )}
                                >
                                  {new Intl.DateTimeFormat(i18next.language, {
                                    weekday: 'short',
                                  }).format(new Date(2025, 0, 5 + d))}
                                </span>
                              </PopoverClose>
                            ))}
                          </div>
                        )}
                        {localRecursPeriod === 'monthly' && (
                          <div className="mt-1 grid grid-cols-7 gap-0.5 border-t border-border pt-1">
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                              <PopoverClose
                                key={d}
                                className="flex h-7 w-7 items-center justify-center rounded cursor-pointer"
                                onClick={() => {
                                  setLocalRecurrenceDay(d)
                                  updateRecursPeriod({
                                    taskId: task.taskId,
                                    recursPeriod: 'monthly',
                                    recurrenceDay: d,
                                    recurrenceTime: recurrenceTimeStr,
                                    recurrenceTzOffset: new Date().getTimezoneOffset(),
                                  }).catch(() => {})
                                }}
                              >
                                <span
                                  className={cn(
                                    'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors hover:bg-muted',
                                    localRecurrenceDay === d
                                      ? 'bg-primary text-primary-foreground'
                                      : 'text-foreground',
                                  )}
                                >
                                  {d}
                                </span>
                              </PopoverClose>
                            ))}
                          </div>
                        )}
                        <div className="mt-1 flex items-center gap-2 border-t border-border px-1 pt-1">
                          <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                          <input
                            type="time"
                            lang="en-GB"
                            className="text-xs bg-transparent outline-none text-foreground"
                            value={recurrenceTimeStr}
                            onChange={(e) => {
                              const s = e.target.value
                              setRecurrenceTimeStr(s)
                              updateRecursPeriod({
                                taskId: task.taskId,
                                recursPeriod: (localRecursPeriod ?? undefined) as
                                  | 'daily'
                                  | 'everyOtherDay'
                                  | 'weekly'
                                  | 'monthly'
                                  | 'weekend'
                                  | 'weekday'
                                  | 'days'
                                  | undefined,
                                recurrenceTime: s,
                                recurrenceTzOffset: new Date().getTimezoneOffset(),
                              }).catch(() => {})
                            }}
                          />
                        </div>
                      </PopoverContent>
                    </Popover>
                  </span>
                )}
                {localDueDate && !task.recurs && !localRecursPeriod ? (
                  <span
                    className={cn(
                      'flex items-center gap-0.5 leading-none',
                      isDueTodayOrOverdue(localDueDate) ? 'text-red-600 dark:text-red-400' : '',
                    )}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {isTaskOverdue(localDueDate, task.allDay) && <OverdueSegment />}
                    <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                      <PopoverTrigger
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className={cn(
                          'flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 transition-colors',
                          isDueTodayOrOverdue(localDueDate)
                            ? 'hover:bg-red-500/10'
                            : 'hover:bg-muted',
                        )}
                      >
                        <CalendarDays className="h-2.5 w-2.5 shrink-0" />
                        {(() => {
                          const d = new Date(localDueDate)
                          const dateStr = d.toLocaleDateString(i18next.language, {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                          })
                          if (task.allDay) return dateStr
                          const start = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
                          if ((task.durationMinutes ?? 0) > 60) {
                            const e2 = new Date(localDueDate + (task.durationMinutes ?? 0) * 60_000)
                            const end = `${String(e2.getHours()).padStart(2, '0')}:${String(e2.getMinutes()).padStart(2, '0')}`
                            return `${dateStr}, ${start} – ${end}`
                          }
                          return `${dateStr}, ${start}`
                        })()}
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[min(20rem,92vw)] p-0"
                        side="bottom"
                        align="start"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex w-full flex-wrap justify-center gap-1 px-3 pt-2 pb-0">
                          {(['today', 'tomorrow', 'thisWeekend', 'nextWeek'] as const).map((p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => handleDatePreset(p)}
                              className="cursor-pointer rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                            >
                              {t(`inbox.datePreset.${p}`)}
                            </button>
                          ))}
                        </div>
                        <Calendar
                          mode="single"
                          disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                          selected={selectedDay}
                          defaultMonth={selectedDay ?? new Date()}
                          onSelect={handleDaySelect}
                          locale={locale}
                          classNames={{
                            root: 'w-full',
                            range_start: '',
                            range_middle: '',
                            range_end: '',
                            today: 'rounded-(--cell-radius) bg-muted text-foreground',
                          }}
                        />
                        <div className="flex w-full items-center justify-center gap-2 border-t border-border px-3 pt-3 pb-1">
                          <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <div
                            className={cn('flex items-center gap-1.5', task.allDay && 'opacity-40')}
                          >
                            <input
                              type="time"
                              lang="en-GB"
                              value={timeStr}
                              onChange={(e) => handleTimeChange(e.target.value)}
                              className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
                            />
                            <span className="text-muted-foreground/40 text-xs select-none">→</span>
                            <input
                              type="time"
                              lang="en-GB"
                              value={shiftTime(timeStr, task.durationMinutes ?? 60)}
                              onChange={(e) => {
                                const newEnd = coerceEnd(timeStr, e.target.value)
                                const dur = Math.max(15, toMinutes(newEnd) - toMinutes(timeStr))
                                updateTaskDuration({
                                  taskId: task.taskId,
                                  durationMinutes: dur,
                                }).catch(() => {})
                              }}
                              className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
                            />
                          </div>
                        </div>
                        <div className="w-full px-3 pb-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setTaskAllDay({ taskId: task.taskId, allDay: !task.allDay }).catch(
                                () => {},
                              )
                            }}
                            className={cn(
                              'flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
                              task.allDay
                                ? 'border-primary/40 bg-primary/10 text-primary-text hover:bg-primary/15'
                                : 'border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground',
                            )}
                          >
                            <Sun className="h-3 w-3 shrink-0" />
                            {t('inbox.allDay')}
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setLocalDueDate(undefined)
                        setSelectedDay(undefined)
                        updateDueDate({ taskId: task.taskId, dueDate: undefined }).catch(() => {})
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className={cn(
                        'flex cursor-pointer items-center rounded px-0.5 py-0.5 text-muted-foreground/60 transition-colors hover:text-foreground',
                        isDueTodayOrOverdue(localDueDate)
                          ? 'hover:bg-red-500/10'
                          : 'hover:bg-muted',
                      )}
                    >
                      <X className="h-2.5 w-2.5" strokeWidth={2.5} />
                    </button>
                  </span>
                ) : null}
              </div>
            </div>
          )}

          {habitCompletions && (
            <div className={cn('mt-1 ml-[30px]', isDone && 'opacity-30')}>
              {numberRow.node}
              {dotsOpen && (
                <HabitStatsLine
                  habitId={task.taskId}
                  stats={habitStats(
                    dotCompletions!,
                    task.recursPeriod,
                    habitCreatedAt,
                    task.recursDays,
                  )}
                />
              )}
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  aria-expanded={dotsOpen}
                  aria-label={t(dotsOpen ? 'today.dotsCollapse' : 'today.dotsExpand')}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleDots(task.taskId as string)
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  className="min-w-0 flex-1 cursor-pointer text-left"
                >
                  <AnimatedHeight>
                    <HabitDotGrid
                      completions={dotCompletions!}
                      color={habitColor(task.category)}
                      period={task.recursPeriod}
                      recursDays={task.recursDays}
                      compact={!dotsOpen}
                      animate={animateDots}
                      createdAt={habitCreatedAt}
                      habitId={task.taskId}
                    />
                  </AnimatedHeight>
                </button>
                {!dotsOpen && (
                  <HabitRowSummary
                    completions={dotCompletions!}
                    period={task.recursPeriod}
                    recursDays={task.recursDays}
                    createdAt={habitCreatedAt}
                    habitId={task.taskId}
                  />
                )}
              </div>
              {flameTip && <FlameTip />}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function TodayListSkeleton() {
  return (
    <div>
      <SkeletonSectionLabel width={52} />
      <SkeletonHabitRow title={62} desc={74} />
      <SkeletonHabitRow title={48} desc={66} />
      <SkeletonSectionLabel width={44} />
      <SkeletonTaskRow title={72} meta={38} />
      <SkeletonTaskRow title={58} meta={30} />
      <SkeletonTaskRow title={66} meta={34} />
    </div>
  )
}

function TodayKanbanSkeleton() {
  return (
    <div className="flex gap-4 items-start">
      {[{ count: 3 }, { count: 2 }, { count: 1 }].map((col, ci) => (
        <div key={ci} className="w-[410px] shrink-0">
          <div className="mb-2 flex items-center gap-1.5">
            <div className="h-2.5 w-16 animate-pulse rounded bg-muted" />
            <div className="h-2.5 w-4 animate-pulse rounded bg-muted" />
          </div>
          <div className="flex flex-col gap-1.5 rounded-lg border border-dashed border-border/60 p-2">
            {Array.from({ length: col.count }).map((_, i) => (
              <div key={i} className="rounded-xl border border-border/60 bg-card px-4 py-3">
                <div className="flex items-start gap-2.5">
                  <div className="mt-0.5 h-5 w-5 shrink-0 animate-pulse rounded-full bg-muted" />
                  <div
                    className="h-3.5 animate-pulse rounded bg-muted"
                    style={{ width: `${55 + i * 13}%` }}
                  />
                </div>
                <div className="mt-1 ml-[30px] flex items-center gap-1.5">
                  <div className="h-2.5 w-12 animate-pulse rounded bg-muted" />
                  <div className="h-2.5 w-20 animate-pulse rounded bg-muted" />
                </div>
                <div className="mt-1.5 ml-[30px] flex items-center justify-between">
                  <div className="h-2.5 w-16 animate-pulse rounded bg-muted" />
                  <div className="h-2.5 w-3 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

type TodayStatus = 'backlog' | 'in-progress' | 'done'

const TODAY_COLUMNS: { id: TodayStatus; labelKey: string }[] = [
  { id: 'backlog', labelKey: 'goals.backlog' },
  { id: 'in-progress', labelKey: 'goals.inProgress' },
  { id: 'done', labelKey: 'goals.done' },
]

const TODAY_COLUMN_IDS = new Set<string>(TODAY_COLUMNS.map((c) => c.id))

type FlatTodayTask = TodayTask & {
  localDone: boolean
  kanbanStatus: TodayStatus
  habitCompletions?: number[]
}

type Habit = {
  _id: Id<'nodes'>
  _creationTime: number
  title: string
  category?: string
  icon: string | null
  description: string | null
  tags: string[]
  effort: 'low' | 'medium' | 'high'
  parentId: Id<'nodes'> | null
  status: 'backlog' | 'in-progress' | 'done' | null
  dueDate: number | null
  durationMinutes: number | null
  recurs: boolean
  isHabit: boolean
  recursPeriod: string
  recursDays?: number[] | null
  recurrenceDay?: number
  recurrenceTime: string | null
  allDay?: boolean
  streakDays: number
  completedAt: number | null
  completions: number[]
  frozen?: boolean
  frozenDays?: string[]
  strength?: number
  fast?: { startedAt: number } | null
  measure?: { fast?: boolean } | null
  oneOff?: boolean
}

// Logical day (honors the user's day-start hour) — used for habit "done today"
// checks and streaks so late-night completions count for the right day.
const localDayStr = (ms: number): string => logicalDayStr(ms)

// Super urgent, then urgent (effort 'high') tasks sort to the top of their section. Stable, so
// non-urgent tasks keep their surfaced order.
const urgentRank = (t: { effort: string; superUrgent?: boolean }) =>
  t.effort === 'high' ? (t.superUrgent ? 2 : 1) : 0
const byUrgentFirst = (
  a: { effort: string; superUrgent?: boolean },
  b: { effort: string; superUrgent?: boolean },
) => urgentRank(b) - urgentRank(a)

// Adapt a habit into a TodayTask so it flows through the kanban board alongside
// focus/quick tasks. Completing a habit column-drag goes through setTaskStatus,
// which logs the per-day completion (filling the dot grid) for recurring tasks.
function habitToTodayTask(
  h: Habit,
  doneToday: boolean,
): TodayTask & { localDone: boolean; habitCompletions: number[] } {
  return {
    taskId: h._id,
    title: h.title,
    icon: h.icon,
    description: h.description,
    tags: h.tags,
    goalTitle: '',
    goalId: h.parentId,
    milestoneTitle: '',
    milestoneId: null,
    targetTitle: '',
    targetId: null,
    category: h.category ?? 'inbox',
    // Habit "done" is per-day: derive it from today's completion (doneToday), NOT
    // the node's persistent completedAt — that field only clears when the backend
    // revival fires, so a habit completed yesterday would otherwise show done today.
    completedAt: doneToday ? (h.completedAt ?? Date.now()) : null,
    effort: h.effort,
    status: h.status ?? 'backlog',
    isInbox: true,
    dueDate: h.dueDate,
    recurs: h.recurs,
    recursPeriod: h.recursPeriod,
    recursDays: h.recursDays ?? null,
    recurrenceDay: h.recurrenceDay ?? null,
    recurrenceTime: h.recurrenceTime,
    durationMinutes: h.durationMinutes,
    allDay: h.allDay ?? false,
    mustDo: false,
    putOffDays: 0,
    localDone: doneToday,
    habitCompletions: h.completions,
  }
}

type TodayListRow =
  | { kind: 'section'; label: string; count?: string }
  | { kind: 'divider' }
  | {
      kind: 'task'
      task: TodayTask & { localDone: boolean }
      taskIndex: number
      must?: 'first' | 'mid' | 'last' | 'only'
    }
  | { kind: 'habit'; habit: Habit; done: boolean; first?: boolean }
  | { kind: 'mustHead'; count: number }
  | { kind: 'nudge'; task: TodayTask }
  | { kind: 'tray'; queued: QueuedTask[] }
  | { kind: 'levelUp' }

function TodayKanbanCard({
  task,
  onDetail,
}: {
  task: FlatTodayTask
  onDetail?: (task: FlatTodayTask) => void
}) {
  const { t } = useTranslation()
  const animateDots = useHabitDotAnimate(
    task.habitCompletions ? (task.taskId as string) : undefined,
  )
  const updateRecursPeriod = useMutation(api.nodes.updateRecursPeriod).withOptimisticUpdate(
    (localStore, args) => {
      const habits = localStore.getQuery(api.nodes.getHabits, {})
      if (habits) {
        localStore.setQuery(
          api.nodes.getHabits,
          {},
          habits.map((h) =>
            h._id === args.taskId
              ? {
                  ...h,
                  ...patchSchedule(h, args),
                  recurrenceTime: args.recurrenceTime ?? h.recurrenceTime,
                }
              : h,
          ),
        )
      }
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (ctx) {
        localStore.setQuery(
          api.dailySurface.getTodayWithContext,
          { localDate },
          {
            ...ctx,
            tasks: ctx.tasks.map((t) =>
              t.taskId === args.taskId
                ? {
                    ...t,
                    ...patchSchedule(t, args),
                    recurrenceTime: args.recurrenceTime ?? t.recurrenceTime,
                  }
                : t,
            ),
          },
        )
      }
    },
  )
  const updateTaskDuration = useMutation(api.nodes.updateTaskDuration).withOptimisticUpdate(
    (localStore, args) => {
      const habits = localStore.getQuery(api.nodes.getHabits, {})
      if (habits) {
        localStore.setQuery(
          api.nodes.getHabits,
          {},
          habits.map((h) =>
            h._id === args.taskId ? { ...h, durationMinutes: args.durationMinutes } : h,
          ),
        )
      }
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (ctx) {
        localStore.setQuery(
          api.dailySurface.getTodayWithContext,
          { localDate },
          {
            ...ctx,
            tasks: ctx.tasks.map((t) =>
              t.taskId === args.taskId ? { ...t, durationMinutes: args.durationMinutes } : t,
            ),
          },
        )
      }
    },
  )
  const updateDueDate = useMutation(api.nodes.updateTaskDueDate).withOptimisticUpdate(
    optimisticDueDate,
  )
  const setTaskAllDay = useMutation(api.nodes.setTaskAllDay).withOptimisticUpdate(
    (localStore, args) => {
      // Habits render from getHabits; recurring tasks from the daily surface.
      const habits = localStore.getQuery(api.nodes.getHabits, {})
      if (habits) {
        localStore.setQuery(
          api.nodes.getHabits,
          {},
          habits.map((h) => (h._id === args.taskId ? { ...h, allDay: args.allDay } : h)),
        )
      }
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (ctx) {
        localStore.setQuery(
          api.dailySurface.getTodayWithContext,
          { localDate },
          {
            ...ctx,
            tasks: ctx.tasks.map((t) =>
              t.taskId === args.taskId ? { ...t, allDay: args.allDay } : t,
            ),
          },
        )
      }
    },
  )
  const [recursOpen, setRecursOpen] = useState(false)
  const [localRecursPeriod, setLocalRecursPeriod] = useState<string | null>(task.recursPeriod)
  const [localRecurrenceDay, setLocalRecurrenceDay] = useState<number>(task.recurrenceDay ?? 1)
  const [localDays, setLocalDays] = useState<number[]>(() =>
    scheduleDays(task.recursPeriod, task.recurrenceDay, task.recursDays),
  )
  const [daysOpen, setDaysOpen] = useState(false)
  const openDays = () => {
    setDaysOpen(true)
    setLocalDays(scheduleDays(localRecursPeriod, localRecurrenceDay, localDays))
  }
  const applyDays = (days: number[]) => {
    const sched = scheduleFromDays(days)
    setLocalDays(days)
    setLocalRecursPeriod(sched.recursPeriod)
    if (sched.recurrenceDay !== undefined) setLocalRecurrenceDay(sched.recurrenceDay)
    updateRecursPeriod({
      taskId: task.taskId,
      recursPeriod: 'days',
      recursDays: days,
      recurrenceTime: recurrenceTimeStr,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
  }
  // Switch a habit from all-day to timed, defaulting to the next full hour.
  const setHabitTime = (start: string, durationMinutes: number) => {
    setRecurrenceTimeStr(start)
    if (localRecursPeriod)
      updateRecursPeriod({
        taskId: task.taskId,
        recursPeriod: localRecursPeriod as
          | 'daily'
          | 'everyOtherDay'
          | 'weekly'
          | 'monthly'
          | 'weekend'
          | 'weekday'
          | 'days',
        recurrenceTime: start,
        recurrenceTzOffset: new Date().getTimezoneOffset(),
      }).catch(() => {})
    updateTaskDuration({ taskId: task.taskId, durationMinutes }).catch(() => {})
    if (task.allDay) setTaskAllDay({ taskId: task.taskId, allDay: false }).catch(() => {})
  }
  const [recurrenceTimeStr, setRecurrenceTimeStr] = useState(task.recurrenceTime ?? '09:00')
  const [dueDateOpen, setDueDateOpen] = useState(false)
  const [localDueDate, setLocalDueDate] = useState<number | undefined>(task.dueDate ?? undefined)
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(
    task.dueDate ? new Date(task.dueDate) : undefined,
  )
  const [timeStr, setTimeStr] = useState(() => {
    if (!task.dueDate) return '09:00'
    const d = new Date(task.dueDate)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  })
  const locale = DATE_FNS_LOCALES[i18next.language.split('-')[0]] ?? enUS

  function applyDueDate(d: Date) {
    setLocalDueDate(d.getTime())
    updateDueDate({ taskId: task.taskId, dueDate: d.getTime() }).catch(() => {})
    setDueDateOpen(false)
  }

  function handleDaySelect(day: Date | undefined) {
    if (!day) return
    setSelectedDay(day)
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    day.setHours(h, m, 0, 0)
    applyDueDate(day)
  }

  function handleDatePreset(preset: 'today' | 'tomorrow' | 'thisWeekend' | 'nextWeek') {
    const d = new Date()
    switch (preset) {
      case 'tomorrow':
        d.setDate(d.getDate() + 1)
        break
      case 'thisWeekend': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 6 ? 0 : 6 - day))
        break
      }
      case 'nextWeek': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 0 ? 1 : 8 - day))
        break
      }
    }
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    d.setHours(h, m, 0, 0)
    applyDueDate(d)
  }

  function handleTimeChange(val: string) {
    setTimeStr(val)
    if (!selectedDay) return
    const [h = 9, m = 0] = val.split(':').map(Number)
    const d = new Date(selectedDay)
    d.setHours(h, m, 0, 0)
    applyDueDate(d)
  }

  useEffect(() => {
    setLocalDueDate(task.dueDate ?? undefined)
    setSelectedDay(task.dueDate ? new Date(task.dueDate) : undefined)
    if (task.dueDate) {
      const d = new Date(task.dueDate)
      setTimeStr(
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      )
    }
  }, [task.dueDate])

  useEffect(() => {
    setLocalRecursPeriod(task.recursPeriod)
    setLocalRecurrenceDay(task.recurrenceDay ?? 1)
    setLocalDays(scheduleDays(task.recursPeriod, task.recurrenceDay, task.recursDays))
    const start = task.recurrenceTime ?? '09:00'
    setRecurrenceTimeStr(start)
  }, [
    task.recursPeriod,
    task.recurrenceDay,
    task.recursDays,
    task.recurrenceTime,
    task.durationMinutes,
  ])

  const isDone = task.kanbanStatus === 'done'
  const displayTitle = task.isInbox ? taskTitle(task) : task.title
  const parsedDesc = task.isInbox ? taskDescription(task) : null
  const parsedTags = task.isInbox ? taskTags(task) : []

  return (
    <div
      className="relative cursor-pointer rounded-xl border border-border/60 bg-card px-4 py-3 transition-colors hover:border-border max-w-full overflow-hidden"
      onClick={() => onDetail?.(task)}
    >
      {/* Title */}
      <div className="flex items-start gap-1.5">
        {task.habitCompletions && <HabitIcon habit={task} />}
        <p
          className={cn(
            'min-w-0 truncate flex-1 text-[15px] font-semibold leading-snug',
            isDone ? 'text-muted-foreground/60 line-through' : 'text-foreground',
          )}
        >
          {displayTitle}
        </p>
        {task.isInbox && task.effort === 'high' && !isDone && (
          <UrgentFlag
            className="mt-[3px] [--dot-ring:var(--card)]"
            superUrgent={task.superUrgent}
            title={task.superUrgent ? undefined : t('filter.priorityUrgent')}
          />
        )}
      </div>
      {task.habitCompletions && <HabitAnchorLineById id={task.taskId} className="mt-1" />}

      {/* Inbox: description */}
      {task.isInbox && parsedDesc && (
        <p
          className={cn(
            'mt-0.5 line-clamp-2 text-[13px] text-muted-foreground',
            isDone && 'opacity-30',
          )}
        >
          <LinkifiedText text={parsedDesc} />
        </p>
      )}

      {/* Inbox: badges row */}
      {task.isInbox && (
        <div
          className={cn(
            'mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[11px] text-muted-foreground',
            isDone && 'opacity-30',
          )}
        >
          {task.habitCompletions && task.category && task.category !== 'inbox' && (
            <CategoryMeta category={task.category} />
          )}
          {(task.recurs || task.recursPeriod) && (
            <span
              className={cn(
                'flex items-center gap-0.5 leading-none',
                !isDone &&
                  !task.allDay &&
                  recurrenceOverdue(task.recurrenceTime ?? '09:00', task.durationMinutes)
                  ? 'text-red-600 dark:text-red-400'
                  : '',
              )}
            >
              {!isDone &&
                !task.allDay &&
                recurrenceOverdue(task.recurrenceTime ?? '09:00', task.durationMinutes) && (
                  <span className="pr-1 font-semibold">{t('inbox.overdue')}</span>
                )}
              <Popover
                open={recursOpen}
                onOpenChange={(o) => {
                  setRecursOpen(o)
                  if (!o) setDaysOpen(false)
                }}
              >
                <PopoverTrigger
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 rounded px-1 py-0.5 cursor-pointer hover:bg-muted transition-colors"
                >
                  <RefreshCw className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
                  {periodLabel(t, i18next.language, localRecursPeriod, localDays)}
                  {localRecursPeriod === 'weekly' && (
                    <span className="text-current/70">
                      {' · '}
                      {new Intl.DateTimeFormat(i18next.language, { weekday: 'short' }).format(
                        new Date(2025, 0, 5 + localRecurrenceDay),
                      )}
                    </span>
                  )}
                  {localRecursPeriod === 'monthly' && (
                    <span className="text-current/70">
                      {' · '}
                      {(() => {
                        if (!i18next.language.startsWith('en')) return localRecurrenceDay
                        try {
                          const r = new Intl.PluralRules(i18next.language, {
                            type: 'ordinal',
                          }).select(localRecurrenceDay)
                          const s: Record<string, string> = {
                            one: 'st',
                            two: 'nd',
                            few: 'rd',
                            other: 'th',
                          }
                          return `${localRecurrenceDay}${s[r] ?? 'th'}`
                        } catch {
                          return localRecurrenceDay
                        }
                      })()}
                    </span>
                  )}
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto min-w-[140px] p-1"
                  side="bottom"
                  align="start"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {(
                    ['daily', 'everyOtherDay', 'weekly', 'monthly', 'weekend', 'weekday'] as const
                  ).map((p) => {
                    const needsDay = p === 'weekly' || p === 'monthly'
                    if (needsDay) {
                      return (
                        <button
                          key={p}
                          type="button"
                          className="w-full"
                          onClick={() => {
                            setLocalRecursPeriod(p)
                            updateRecursPeriod({
                              taskId: task.taskId,
                              recursPeriod: p,
                              recurrenceTime: recurrenceTimeStr,
                              recurrenceTzOffset: new Date().getTimezoneOffset(),
                              recurrenceDay: localRecurrenceDay,
                            }).catch(() => {})
                          }}
                        >
                          <span
                            className={cn(
                              'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
                              localRecursPeriod === p ? 'text-primary-text' : 'text-foreground',
                            )}
                          >
                            <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
                            {t(`inbox.period.${p}`)}
                          </span>
                        </button>
                      )
                    }
                    return (
                      <PopoverClose
                        key={p}
                        className="w-full"
                        onClick={() => {
                          setLocalRecursPeriod(p)
                          updateRecursPeriod({
                            taskId: task.taskId,
                            recursPeriod: p,
                            recurrenceTime: recurrenceTimeStr,
                            recurrenceTzOffset: new Date().getTimezoneOffset(),
                          }).catch(() => {})
                        }}
                      >
                        <span
                          className={cn(
                            'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
                            localRecursPeriod === p ? 'text-primary-text' : 'text-foreground',
                          )}
                        >
                          <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
                          {t(`inbox.period.${p}`)}
                        </span>
                      </PopoverClose>
                    )
                  })}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      openDays()
                    }}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
                      daysOpen || localRecursPeriod === 'days'
                        ? 'text-primary-text'
                        : 'text-foreground',
                    )}
                  >
                    <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
                    {t('inbox.period.days')}
                  </button>
                  {(daysOpen || localRecursPeriod === 'days') && (
                    <RecursDaysChips
                      className="mt-1 border-t border-border/40 px-2 py-2"
                      days={localDays}
                      onChange={applyDays}
                    />
                  )}
                  {localRecursPeriod === 'weekly' && !daysOpen && (
                    <div className="flex gap-0.5 px-2 py-2 border-t border-border/40 mt-1">
                      {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                        <PopoverClose
                          key={d}
                          className="flex h-7 w-7 items-center justify-center rounded cursor-pointer"
                          onClick={() => {
                            setLocalRecurrenceDay(d)
                            updateRecursPeriod({
                              taskId: task.taskId,
                              recursPeriod: 'weekly',
                              recurrenceDay: d,
                              recurrenceTime: recurrenceTimeStr,
                              recurrenceTzOffset: new Date().getTimezoneOffset(),
                            }).catch(() => {})
                          }}
                        >
                          <span
                            className={cn(
                              'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors hover:bg-muted',
                              localRecurrenceDay === d
                                ? 'bg-primary text-primary-foreground'
                                : 'text-foreground',
                            )}
                          >
                            {new Intl.DateTimeFormat(i18next.language, {
                              weekday: 'narrow',
                            }).format(new Date(2025, 0, 5 + d))}
                          </span>
                        </PopoverClose>
                      ))}
                    </div>
                  )}
                  {localRecursPeriod === 'monthly' && (
                    <div className="grid grid-cols-7 gap-0.5 px-2 py-2 border-t border-border/40 mt-1 w-[200px]">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                        <PopoverClose
                          key={d}
                          className="flex h-7 w-7 items-center justify-center rounded cursor-pointer"
                          onClick={() => {
                            setLocalRecurrenceDay(d)
                            updateRecursPeriod({
                              taskId: task.taskId,
                              recursPeriod: 'monthly',
                              recurrenceDay: d,
                              recurrenceTime: recurrenceTimeStr,
                              recurrenceTzOffset: new Date().getTimezoneOffset(),
                            }).catch(() => {})
                          }}
                        >
                          <span
                            className={cn(
                              'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors hover:bg-muted',
                              localRecurrenceDay === d
                                ? 'bg-primary text-primary-foreground'
                                : 'text-foreground',
                            )}
                          >
                            {d}
                          </span>
                        </PopoverClose>
                      ))}
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              <HabitTimePicker
                className="ml-1"
                allDay={!!task.allDay}
                start={recurrenceTimeStr}
                durationMinutes={task.durationMinutes ?? 60}
                overdue={
                  !isDone &&
                  !task.allDay &&
                  recurrenceOverdue(task.recurrenceTime ?? '09:00', task.durationMinutes)
                }
                onSet={setHabitTime}
                onAllDay={() =>
                  setTaskAllDay({ taskId: task.taskId, allDay: true }).catch(() => {})
                }
              />
            </span>
          )}
          {localDueDate && !task.recurs && !task.recursPeriod && (
            <span
              className={cn(
                'flex items-center gap-0.5 leading-none',
                isDueTodayOrOverdue(localDueDate) ? 'text-red-600 dark:text-red-400' : '',
              )}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {isTaskOverdue(localDueDate, task.allDay) && <OverdueSegment />}
              <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                <PopoverTrigger
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={cn(
                    'flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 transition-colors',
                    isDueTodayOrOverdue(localDueDate) ? 'hover:bg-red-500/10' : 'hover:bg-muted',
                  )}
                >
                  <CalendarDays className="h-2.5 w-2.5 shrink-0" />
                  {(() => {
                    const d = new Date(localDueDate)
                    const dateStr = d.toLocaleDateString(i18next.language, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })
                    if (task.allDay) return dateStr
                    const start = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
                    if ((task.durationMinutes ?? 0) > 60) {
                      const e2 = new Date(localDueDate + (task.durationMinutes ?? 0) * 60_000)
                      const end = `${String(e2.getHours()).padStart(2, '0')}:${String(e2.getMinutes()).padStart(2, '0')}`
                      return `${dateStr}, ${start} – ${end}`
                    }
                    return `${dateStr}, ${start}`
                  })()}
                </PopoverTrigger>
                <PopoverContent
                  className="w-[min(20rem,92vw)] p-0"
                  side="bottom"
                  align="start"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex w-full flex-wrap justify-center gap-1 px-3 pt-2 pb-0">
                    {(['today', 'tomorrow', 'thisWeekend', 'nextWeek'] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => handleDatePreset(p)}
                        className="cursor-pointer rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                      >
                        {t(`inbox.datePreset.${p}`)}
                      </button>
                    ))}
                  </div>
                  <Calendar
                    mode="single"
                    disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                    selected={selectedDay}
                    defaultMonth={selectedDay ?? new Date()}
                    onSelect={handleDaySelect}
                    locale={locale}
                    classNames={{
                      root: 'w-full',
                      range_start: '',
                      range_middle: '',
                      range_end: '',
                      today: 'rounded-(--cell-radius) bg-muted text-foreground',
                    }}
                  />
                  <div className="flex w-full items-center justify-center gap-2 border-t border-border px-3 pt-3 pb-1">
                    <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <div className={cn('flex items-center gap-1.5', task.allDay && 'opacity-40')}>
                      <input
                        type="time"
                        lang="en-GB"
                        value={timeStr}
                        onChange={(e) => handleTimeChange(e.target.value)}
                        className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
                      />
                      <span className="text-muted-foreground/40 text-xs select-none">→</span>
                      <input
                        type="time"
                        lang="en-GB"
                        value={shiftTime(timeStr, task.durationMinutes ?? 60)}
                        onChange={(e) => {
                          const newEnd = coerceEnd(timeStr, e.target.value)
                          const dur = Math.max(15, toMinutes(newEnd) - toMinutes(timeStr))
                          updateTaskDuration({ taskId: task.taskId, durationMinutes: dur }).catch(
                            () => {},
                          )
                        }}
                        className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
                      />
                    </div>
                  </div>
                  <div className="w-full px-3 pb-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setTaskAllDay({ taskId: task.taskId, allDay: !task.allDay }).catch(() => {})
                      }}
                      className={cn(
                        'flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
                        task.allDay
                          ? 'border-primary/40 bg-primary/10 text-primary-text hover:bg-primary/15'
                          : 'border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground',
                      )}
                    >
                      <Sun className="h-3 w-3 shrink-0" />
                      {t('inbox.allDay')}
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setLocalDueDate(undefined)
                  setSelectedDay(undefined)
                  updateDueDate({ taskId: task.taskId, dueDate: undefined }).catch(() => {})
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className={cn(
                  'flex cursor-pointer items-center rounded px-0.5 py-0.5 text-muted-foreground/60 transition-colors hover:text-foreground',
                  isDueTodayOrOverdue(localDueDate) ? 'hover:bg-red-500/10' : 'hover:bg-muted',
                )}
              >
                <X className="h-2.5 w-2.5" strokeWidth={2.5} />
              </button>
            </span>
          )}
          {task.location && <LocationMeta location={task.location} />}
          {parsedTags.map((tag) => (
            <span key={tag} className="leading-none min-w-0 truncate" title={`#${tag}`}>
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Goal task: category · goal */}
      {!task.isInbox && (task.category || task.goalTitle) && (
        <div
          className={cn(
            'mt-1 flex items-center gap-2.5 overflow-hidden font-mono text-[11px] text-muted-foreground',
            isDone && 'opacity-30',
          )}
        >
          {task.category && <CategoryMeta category={task.category} />}
          {task.goalTitle && <p className="min-w-0 truncate">{task.goalTitle}</p>}
        </div>
      )}

      {/* Goal task: effort · recurrence · due date */}
      {!task.isInbox && (
        <div className={cn('mt-1 flex items-center justify-between', isDone && 'opacity-30')}>
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[11px] text-muted-foreground">
            <span className="shrink-0">
              {exactTaskMinutes(task)
                ? taskTimeLabel(task)
                : `${taskTimeLabel(task)} · ${t(`stats.effort${task.effort.charAt(0).toUpperCase() + task.effort.slice(1)}`)}`}
            </span>
            {(task.recurs || localRecursPeriod) && (
              <span className="flex items-center leading-none">
                <Popover
                  open={recursOpen}
                  onOpenChange={(o) => {
                    setRecursOpen(o)
                    if (!o) setDaysOpen(false)
                  }}
                >
                  <PopoverTrigger
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 rounded px-1 py-0.5 cursor-pointer hover:bg-muted transition-colors"
                  >
                    <RefreshCw className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
                    {periodLabel(t, i18next.language, localRecursPeriod, localDays)}
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto min-w-[140px] p-1"
                    side="bottom"
                    align="start"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {(
                      ['daily', 'everyOtherDay', 'weekly', 'monthly', 'weekend', 'weekday'] as const
                    ).map((p) => (
                      <PopoverClose
                        key={p}
                        onClick={(e) => {
                          e.stopPropagation()
                          setLocalRecursPeriod(p)
                          updateRecursPeriod({
                            taskId: task.taskId,
                            recursPeriod: p,
                            recurrenceTime: recurrenceTimeStr,
                            recurrenceTzOffset: new Date().getTimezoneOffset(),
                          }).catch(() => {})
                        }}
                        className={cn(
                          'w-full cursor-pointer rounded px-2 py-1 text-left text-xs transition-colors hover:bg-muted',
                          localRecursPeriod === p ? 'text-primary-text' : 'text-foreground',
                        )}
                      >
                        {t(`inbox.period.${p}`)}
                      </PopoverClose>
                    ))}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        openDays()
                      }}
                      className={cn(
                        'w-full cursor-pointer rounded px-2 py-1 text-left text-xs transition-colors hover:bg-muted',
                        daysOpen || localRecursPeriod === 'days'
                          ? 'text-primary-text'
                          : 'text-foreground',
                      )}
                    >
                      {t('inbox.period.days')}
                    </button>
                    {(daysOpen || localRecursPeriod === 'days') && (
                      <RecursDaysChips
                        className="mt-1 border-t border-border/40 px-2 py-2"
                        days={localDays}
                        onChange={applyDays}
                      />
                    )}
                    <div className="mt-1 flex items-center gap-2 border-t border-border px-1 pt-1">
                      <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <input
                        type="time"
                        lang="en-GB"
                        className="text-xs bg-transparent outline-none text-foreground"
                        value={recurrenceTimeStr}
                        onChange={(e) => {
                          const s = e.target.value
                          setRecurrenceTimeStr(s)
                          updateRecursPeriod({
                            taskId: task.taskId,
                            recursPeriod: (localRecursPeriod ?? undefined) as
                              | 'daily'
                              | 'everyOtherDay'
                              | 'weekly'
                              | 'monthly'
                              | 'weekend'
                              | 'weekday'
                              | 'days'
                              | undefined,
                            recurrenceTime: s,
                            recurrenceTzOffset: new Date().getTimezoneOffset(),
                          }).catch(() => {})
                        }}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </span>
            )}
            {localDueDate && !task.recurs && !localRecursPeriod ? (
              <span
                className={cn(
                  'flex items-center gap-0.5 leading-none',
                  isDueTodayOrOverdue(localDueDate) ? 'text-red-600 dark:text-red-400' : '',
                )}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {isTaskOverdue(localDueDate, task.allDay) && <OverdueSegment />}
                <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                  <PopoverTrigger
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={cn(
                      'flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 transition-colors',
                      isDueTodayOrOverdue(localDueDate) ? 'hover:bg-red-500/10' : 'hover:bg-muted',
                    )}
                  >
                    <CalendarDays className="h-2.5 w-2.5 shrink-0" />
                    {(() => {
                      const d = new Date(localDueDate)
                      const dateStr = d.toLocaleDateString(i18next.language, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })
                      if (task.allDay) return dateStr
                      const start = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
                      if ((task.durationMinutes ?? 0) > 60) {
                        const e2 = new Date(localDueDate + (task.durationMinutes ?? 0) * 60_000)
                        const end = `${String(e2.getHours()).padStart(2, '0')}:${String(e2.getMinutes()).padStart(2, '0')}`
                        return `${dateStr}, ${start} – ${end}`
                      }
                      return `${dateStr}, ${start}`
                    })()}
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[min(20rem,92vw)] p-0"
                    side="bottom"
                    align="start"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="flex w-full flex-wrap justify-center gap-1 px-3 pt-2 pb-0">
                      {(['today', 'tomorrow', 'thisWeekend', 'nextWeek'] as const).map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => handleDatePreset(p)}
                          className="cursor-pointer rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          {t(`inbox.datePreset.${p}`)}
                        </button>
                      ))}
                    </div>
                    <Calendar
                      mode="single"
                      disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                      selected={selectedDay}
                      defaultMonth={selectedDay ?? new Date()}
                      onSelect={handleDaySelect}
                      locale={locale}
                      classNames={{
                        root: 'w-full',
                        range_start: '',
                        range_middle: '',
                        range_end: '',
                        today: 'rounded-(--cell-radius) bg-muted text-foreground',
                      }}
                    />
                    <div className="flex w-full items-center justify-center gap-2 border-t border-border px-3 pt-3 pb-1">
                      <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <div className={cn('flex items-center gap-1.5', task.allDay && 'opacity-40')}>
                        <input
                          type="time"
                          lang="en-GB"
                          value={timeStr}
                          onChange={(e) => handleTimeChange(e.target.value)}
                          className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
                        />
                        <span className="text-muted-foreground/40 text-xs select-none">→</span>
                        <input
                          type="time"
                          lang="en-GB"
                          value={shiftTime(timeStr, task.durationMinutes ?? 60)}
                          onChange={(e) => {
                            const newEnd = coerceEnd(timeStr, e.target.value)
                            const dur = Math.max(15, toMinutes(newEnd) - toMinutes(timeStr))
                            updateTaskDuration({ taskId: task.taskId, durationMinutes: dur }).catch(
                              () => {},
                            )
                          }}
                          className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
                        />
                      </div>
                    </div>
                    <div className="w-full px-3 pb-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setTaskAllDay({ taskId: task.taskId, allDay: !task.allDay }).catch(
                            () => {},
                          )
                        }}
                        className={cn(
                          'flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
                          task.allDay
                            ? 'border-primary/40 bg-primary/10 text-primary-text hover:bg-primary/15'
                            : 'border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Sun className="h-3 w-3 shrink-0" />
                        {t('inbox.allDay')}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setLocalDueDate(undefined)
                    setSelectedDay(undefined)
                    updateDueDate({ taskId: task.taskId, dueDate: undefined }).catch(() => {})
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={cn(
                    'flex cursor-pointer items-center rounded px-0.5 py-0.5 text-muted-foreground/60 transition-colors hover:text-foreground',
                    isDueTodayOrOverdue(localDueDate) ? 'hover:bg-red-500/10' : 'hover:bg-muted',
                  )}
                >
                  <X className="h-2.5 w-2.5" strokeWidth={2.5} />
                </button>
              </span>
            ) : null}
          </div>
          <span className="text-xs text-muted-foreground/40 shrink-0">→</span>
        </div>
      )}

      {task.habitCompletions && (
        <div className={cn('mt-2 flex items-center gap-2.5', isDone && 'opacity-30')}>
          <div className="min-w-0 flex-1">
            <HabitDotGrid
              completions={task.habitCompletions}
              color={habitColor(task.category)}
              period={task.recursPeriod}
              recursDays={task.recursDays}
              compact
              animate={animateDots}
              habitId={task.taskId}
            />
          </div>
          <HabitRowSummary
            completions={task.habitCompletions}
            period={task.recursPeriod}
            recursDays={task.recursDays}
            habitId={task.taskId}
          />
        </div>
      )}
    </div>
  )
}

function SortableTodayCard({
  task,
  onDetail,
}: {
  task: FlatTodayTask
  onDetail?: (task: FlatTodayTask) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.taskId as string,
    data: { type: 'task', status: task.kanbanStatus },
    disabled: task.kanbanStatus === 'done',
  })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      {...attributes}
      {...listeners}
      className={task.kanbanStatus === 'done' ? 'cursor-default' : 'cursor-pointer'}
    >
      <TodayKanbanCard task={task} onDetail={onDetail} />
    </div>
  )
}

function TodayKanbanColumn({
  column,
  tasks,
  isDragTarget,
  onDetail,
}: {
  column: { id: TodayStatus; labelKey: string }
  tasks: FlatTodayTask[]
  isDragTarget?: boolean
  onDetail?: (task: FlatTodayTask) => void
}) {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({ id: column.id, data: { type: 'column' } })
  return (
    <div className="flex w-[410px] shrink-0 flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t(column.labelKey)}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">{tasks.length}</span>
      </div>
      <SortableContext
        id={column.id}
        items={tasks.map((t) => t.taskId as string)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={setNodeRef}
          className={cn(
            'flex min-h-[102px] flex-col gap-1.5 rounded-lg border border-dashed p-2 transition-colors',
            isOver || isDragTarget ? 'border-primary/50 bg-primary/5' : 'border-border/60',
          )}
        >
          {tasks.map((task) => (
            <SortableTodayCard key={task.taskId as string} task={task} onDetail={onDetail} />
          ))}
        </div>
      </SortableContext>
    </div>
  )
}

function TodayKanban({
  tasks,
  onDetail,
}: {
  tasks: (TodayTask & { localDone: boolean; habitCompletions?: number[] })[]
  onDetail?: (task: FlatTodayTask) => void
}) {
  const setTaskStatus = useSetTaskStatus()

  const flatTasks: FlatTodayTask[] = tasks.map((t) => ({
    ...t,
    kanbanStatus: t.localDone || t.completedAt !== null ? 'done' : (t.status as TodayStatus),
  }))

  const [localTasks, setLocalTasks] = useState<FlatTodayTask[]>(flatTasks)
  const [activeTask, setActiveTask] = useState<FlatTodayTask | null>(null)
  const pendingMutationRef = useRef(false)

  useEffect(() => {
    document.body.classList.toggle('kanban-dragging', !!activeTask)
    return () => {
      document.body.classList.remove('kanban-dragging')
    }
  }, [activeTask])

  const flatKey = flatTasks.map((t) => `${t.taskId as string}:${t.kanbanStatus}`).join(',')
  const localKey = localTasks.map((t) => `${t.taskId as string}:${t.kanbanStatus}`).join(',')
  useEffect(() => {
    // Don't revert optimistic update while a mutation is in flight
    if (!activeTask && !pendingMutationRef.current && flatKey !== localKey) setLocalTasks(flatTasks)
  }, [flatKey, activeTask])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const byColumn = (status: TodayStatus) => localTasks.filter((t) => t.kanbanStatus === status)

  function resolveTargetStatus(over: DragOverEvent['over']): TodayStatus {
    if (!over) return 'backlog'
    const id = over.id as string
    if (TODAY_COLUMN_IDS.has(id)) return id as TodayStatus
    return localTasks.find((t) => (t.taskId as string) === id)?.kanbanStatus ?? 'backlog'
  }

  function handleDragStart({ active }: DragStartEvent) {
    haptic()
    setActiveTask(localTasks.find((t) => (t.taskId as string) === active.id) ?? null)
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    if (!over) return
    const item = localTasks.find((t) => (t.taskId as string) === active.id)
    if (!item || item.kanbanStatus === 'done') return
    const target = resolveTargetStatus(over)
    if (item.kanbanStatus === target) return
    setLocalTasks((prev) =>
      prev.map((t) => ((t.taskId as string) === active.id ? { ...t, kanbanStatus: target } : t)),
    )
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    const original = flatTasks.find((t) => (t.taskId as string) === active.id)
    if (original && !original.kanbanStatus.includes('done') && over) {
      const target = resolveTargetStatus(over)
      if (target !== original.kanbanStatus) {
        // Apply optimistic update before clearing active to prevent snap-back
        setLocalTasks((prev) =>
          prev.map((t) =>
            (t.taskId as string) === active.id ? { ...t, kanbanStatus: target } : t,
          ),
        )
        if (target === 'done') playDoneSound()
        pendingMutationRef.current = true
        setTaskStatus({ taskId: active.id as Id<'nodes'>, status: target })
          .catch(() => {
            toast.error("Couldn't update task — please try again.")
            setLocalTasks(flatTasks)
          })
          .finally(() => {
            pendingMutationRef.current = false
          })
      }
    }
    setActiveTask(null)
  }

  // Track which column the dragged card currently occupies for the drop indicator
  const activeTargetStatus = activeTask
    ? (localTasks.find((t) => (t.taskId as string) === (activeTask.taskId as string))
        ?.kanbanStatus ?? null)
    : null

  return (
    <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 items-start">
          {TODAY_COLUMNS.map((col) => (
            <TodayKanbanColumn
              key={col.id}
              column={col}
              tasks={byColumn(col.id)}
              isDragTarget={activeTargetStatus === col.id}
              onDetail={onDetail}
            />
          ))}
        </div>
        {/* Portaled: the tab track's transform would re-anchor the fixed overlay. */}
        {createPortal(
          <DragOverlay>
            {activeTask && (
              <div className="rotate-1 opacity-90">
                <TodayKanbanCard task={activeTask} />
              </div>
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </div>
  )
}

// Calm full-screen state shown on Today while on a breather.
function BreatherScreen({ until, onEnd }: { until?: string; onEnd: () => void }) {
  const { t } = useTranslation()
  const untilLabel = until
    ? new Date(`${until}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : null
  return (
    <div className="flex h-full flex-col items-center justify-center bg-background px-6 text-center">
      <Sprout className="size-14 text-primary-text" strokeWidth={1.75} />
      <h1 className="mt-5 text-2xl font-bold text-foreground">{t('breather.title')}</h1>
      <p className="mt-2 max-w-xs text-sm text-muted-foreground">
        {untilLabel ? t('breather.untilBody', { date: untilLabel }) : t('breather.indefiniteBody')}
      </p>
      <p className="mt-1 max-w-xs text-sm text-muted-foreground">{t('breather.reassure')}</p>
      <button
        type="button"
        onClick={onEnd}
        className="mt-8 cursor-pointer rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {t('breather.end')}
      </button>
    </div>
  )
}

// One-time re-entry card on Today after a breather ends. "Freshen up my plan"
// opens the coach with a seeded message — the AI-native return experience.
function WelcomeBackCard({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="relative rounded-xl border border-primary/30 bg-primary/5 p-4">
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('breather.dismiss')}
        className="absolute right-2 top-2 cursor-pointer text-muted-foreground/50 transition-colors hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
      <p className="text-sm font-semibold text-foreground">{t('breather.welcomeTitle')}</p>
      <p className="mt-1 pr-4 text-xs text-muted-foreground">{t('breather.welcomeBody')}</p>
      <button
        type="button"
        onClick={() => {
          window.dispatchEvent(
            new CustomEvent('open-coach', { detail: { message: t('breather.freshenSeed') } }),
          )
          onDismiss()
        }}
        className="mt-3 cursor-pointer rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {t('breather.freshen')}
      </button>
    </div>
  )
}

function TodayContent({ view }: { view: 'list' | 'kanban' }) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const posthog = usePostHog()
  const navigate = useNavigate()
  const state = useTodayTasks()
  const onTodayTab = Route.useSearch({ select: (s) => (s.tab ?? 'today') === 'today' })
  const queued = state.status === 'tasks' || state.status === 'all-completed' ? state.queued : []
  const mustDo = useMustDo()
  const [nudgeHidden, setNudgeHidden] = useState<Set<string>>(() => new Set())
  const breather = useBreather({ autoEnd: true })
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((localStore, { taskId }) =>
    completeNode(localStore, taskId),
  )
  const skipTodayMut = useMutation(api.dailySurface.skipToday)
  const unskipTodayMut = useMutation(api.dailySurface.unskipToday)
  const online = useOnlineStatus()
  const enqueueOp = useOfflineStore((s) => s.enqueue)
  const dismissNudge = useMutation(api.coachNudge.dismiss).withOptimisticUpdate((localStore) => {
    for (const { args, value } of localStore.getAllQueries(api.dailySurface.getTodayWithContext))
      if (value?.activeNudge)
        localStore.setQuery(api.dailySurface.getTodayWithContext, args, {
          ...value,
          activeNudge: null,
        })
  })
  const storeLocation = useMutation(api.userLocation.store)
  const existingLocation = useQuery(api.userLocation.get)
  const locationDismissed = useViewStore((s) => s.locationCardDismissed)
  const dismissLocationCard = useViewStore((s) => s.dismissLocationCard)
  const calendarConnections = useQuery(api.calendar.getConnectionStatus)
  const calendarCardDismissed = useViewStore((s) => s.calendarCardDismissed)
  const dismissCalendarCard = useViewStore((s) => s.dismissCalendarCard)
  const habitsLive = useQuery(api.nodes.getHabits) as Habit[] | undefined
  const habitsCacheRef = useRef<Habit[] | undefined>(undefined)
  if (habitsCacheRef.current === undefined)
    habitsCacheRef.current = readSnapshot<Habit[]>(HABITS_CACHE_KEY) ?? lastKnownHabits ?? undefined
  useEffect(() => {
    if (habitsLive !== undefined) {
      writeSnapshot(HABITS_CACHE_KEY, habitsLive)
      lastKnownHabits = habitsLive
      habitsCacheRef.current = habitsLive
    }
  }, [habitsLive])
  const habitsData = habitsLive ?? habitsCacheRef.current
  const todayCompact = useViewStore((s) => s.todayCompact)
  const toggleTodayCompact = useViewStore((s) => s.toggleTodayCompact)
  const goalsTree = useQuery(api.nodes.getGoalsFullTree, todayCompact ? {} : 'skip')
  // Which Today sections the user wants shown (all default on).
  const todayPrefs = useQuery(api.preferences.getMyPreferences) as
    | {
        todayShowFocus?: boolean
        todayShowHabits?: boolean
        todayShowQuick?: boolean
        todaySectionOrder?: string[]
        onboardingCompletedAt?: number
        focus?: string
      }
    | undefined
  const sectionOrder = resolveTodaySectionOrder(todayPrefs?.todaySectionOrder)
  const habitsFocus = todayPrefs?.focus === 'habits'
  const showFocus = todayPrefs?.todayShowFocus !== false
  const showHabits = todayPrefs?.todayShowHabits !== false
  const showQuick = todayPrefs?.todayShowQuick !== false
  const todayStr = localDayStr(Date.now())
  const onboardedAt = todayPrefs?.onboardingCompletedAt
  const onboardedToday = !!onboardedAt && localDayStr(onboardedAt) === todayStr
  // "Start here" goal, set when onboarding finishes or a template is used;
  // cleared on its first completed task, and expires with the day it was set.
  const [startHereGoal, setStartHereGoal] = useState(startHereGoalId)
  // Habits the user dismissed from Today ("not today") — hidden here for the day,
  // still completable from the inbox Habits tab. Never affects the streak.
  const skipHabitTodayMut = useMutation(api.dailySurface.skipHabitToday)
  const unskipHabitTodayMut = useMutation(api.dailySurface.unskipHabitToday)
  // Optimistic so Undo clears completedAt immediately — otherwise the surface's
  // done/all-done filtering still treats the task as done for a frame and it
  // flickers (reappears → vanishes → reappears) while the mutation round-trips.
  const uncompleteTaskMut = useMutation(api.nodes.uncomplete).withOptimisticUpdate(
    (localStore, { taskId }) => {
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (!ctx) return
      localStore.setQuery(
        api.dailySurface.getTodayWithContext,
        { localDate },
        {
          ...ctx,
          tasks: ctx.tasks.map((t) => (t.taskId === taskId ? { ...t, completedAt: null } : t)),
          allCompleted: false,
        },
      )
    },
  )
  const rawHabitSkips = useQuery(api.dailySurface.getTodayHabitSkips, { localDate: todayStr })
  useEffect(() => {
    if (rawHabitSkips !== undefined) {
      lastHabitSkips = { date: todayStr, ids: rawHabitSkips.map(String) }
      writeSnapshot(HABIT_SKIPS_CACHE_KEY, lastHabitSkips)
    }
  }, [rawHabitSkips, todayStr])
  // Fall back to the cached skip set (module, then localStorage on reload) so a
  // skipped habit isn't shown/counted for a frame while the query reloads.
  if (lastHabitSkips === null)
    lastHabitSkips = readSnapshot<{ date: string; ids: string[] }>(HABIT_SKIPS_CACHE_KEY) ?? null
  const skippedHabitIds = new Set(
    rawHabitSkips !== undefined
      ? rawHabitSkips.map(String)
      : lastHabitSkips?.date === todayStr
        ? lastHabitSkips.ids
        : [],
  )
  // Stable order (getHabits returns creation order). Intentionally NOT sorted by
  // recurrence time — editing a time would make the row jump and lose the place.
  const hasRealGoal = (useGoals() ?? []).some((g) => !g.isInbox)
  const dueHabits = (habitsData ?? []).filter(
    (h) => !h.frozen && habitDueOn(h, keyToDate(todayStr)) && !skippedHabitIds.has(h._id as string),
  )
  // Habits due yesterday that never got checked off (late-night ones, usually).
  const yk = yesterdayKey()
  const missedYesterday = (habitsData ?? [])
    .filter(
      (h) =>
        !h.frozenDays?.includes(yk) &&
        !h.measure?.fast &&
        localDayStr(h._creationTime) <= yk &&
        dueOn(h.recursPeriod, h.recurrenceDay, yesterdayDate(), h.recursDays) &&
        !doneOnDay(h.completions, yk),
    )
    .map((h) => ({ id: h._id, title: h.title }))
  const streakInfo = useQuery(api.nodes.getUserStreakInfo)
  const streak = streakInfo?.streak ?? 0
  const streakFreezes = streakInfo?.freezes ?? 0
  const frozeLastNight = streakInfo?.frozeLastNight ?? false
  const reviewStatus = useQuery(api.weeklyReview.getReviewStatus)
  const showReviewPrompt = reviewStatus?.localDay === 0 && !reviewStatus.alreadyReviewed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const likesApi = api as any
  const allTaskIds = state.status === 'tasks' ? state.tasks.map((t) => t.taskId as string) : []
  const likeCounts = useQuery(
    likesApi.taskLikes.getTaskLikeCounts,
    allTaskIds.length > 0 ? { taskIds: allTaskIds } : 'skip',
  ) as Array<{ taskId: string; count: number }> | undefined
  const likeCountMap = new Map((likeCounts ?? []).map((l) => [l.taskId, l.count]))
  const [localDone, setLocalDone] = useState<Set<string>>(new Set())
  const [dismissing, setDismissing] = useState<Set<string>>(new Set())
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [skippingIds, setSkippingIds] = useState<Set<string>>(new Set())

  // A habit dismissed here after completion stays hidden via localDone/dismissed.
  // If it's later uncompleted elsewhere (e.g. the inbox), those sets would keep it
  // hidden forever — so once a habit is no longer done today, drop it from them so
  // it returns to the pending list. Only prune habit ids; task ids are left alone.
  useEffect(() => {
    const habitIds = new Set(dueHabits.map((h) => h._id as string))
    const doneTodayIds = new Set(
      dueHabits
        .filter((h) => h.completions.some((ms) => localDayStr(ms) === todayStr))
        .map((h) => h._id as string),
    )
    const prune = (prev: Set<string>): Set<string> => {
      let changed = false
      const next = new Set(prev)
      for (const id of prev) {
        if (habitIds.has(id) && !doneTodayIds.has(id)) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : prev
    }
    setLocalDone(prune)
    setDismissing(prune)
    setDismissed(prune)
  }, [dueHabits, todayStr])
  const [selectedTask, setSelectedTask] = useState<(TodayTask & { localDone: boolean }) | null>(
    null,
  )
  const [selectedHabitId, setSelectedHabitId] = useState<Id<'nodes'> | null>(null)
  const selectedHabit = (habitsData ?? []).find((h) => h._id === selectedHabitId) ?? null
  const habitForSheet: InboxTaskForSheet | null = selectedHabit
    ? {
        _id: selectedHabit._id,
        title: selectedHabit.title,
        description: selectedHabit.description ?? undefined,
        tags: selectedHabit.tags,
        effort: selectedHabit.effort,
        // Per-day done state (see habitToTodayTask): only "done" if completed today,
        // not from the node's persistent completedAt.
        completedAt: selectedHabit.completions.some((ms) => localDayStr(ms) === todayStr)
          ? (selectedHabit.completedAt ?? Date.now())
          : null,
        recursPeriod: selectedHabit.recursPeriod,
        recursDays: selectedHabit.recursDays,
        recurs: selectedHabit.recurs,
        recurrenceTime: selectedHabit.recurrenceTime ?? undefined,
        recurrenceDay: selectedHabit.recurrenceDay,
        dueDate: selectedHabit.dueDate ?? undefined,
        durationMinutes: selectedHabit.durationMinutes ?? undefined,
        allDay: selectedHabit.allDay,
        status: selectedHabit.status ?? undefined,
        parentId: selectedHabit.parentId,
        isHabit: selectedHabit.isHabit,
        category: selectedHabit.category,
      }
    : null
  const completeHabit = useMutation(api.nodes.complete).withOptimisticUpdate(
    (localStore, { taskId }) => optimisticHabitCompletion(localStore, taskId as string, true),
  )
  const uncompleteHabit = useMutation(api.nodes.uncomplete).withOptimisticUpdate(
    (localStore, { taskId }) => optimisticHabitCompletion(localStore, taskId as string, false),
  )
  const fast = useFastActions()
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const kbStateRef = useRef<{
    navigable: (TodayTask & { localDone: boolean })[]
    selectedTask: (TodayTask & { localDone: boolean }) | null
    focusedTaskId: string | null
    helpOpen: boolean
    handleComplete: (id: Id<'nodes'>) => void
    setSelectedTask: (t: TodayTask & { localDone: boolean }) => void
    setSelectedHabitId: (id: Id<'nodes'>) => void
    habitIds: Set<string>
    setHelpOpen: (v: boolean) => void
  } | null>(null)
  const allDoneRef = useRef<boolean | null>(null)
  const earlyAllDone =
    state.status === 'all-completed' ||
    (state.status === 'tasks' &&
      state.tasks.filter(
        (t) =>
          !localDone.has(t.taskId as string) &&
          !dismissed.has(t.taskId as string) &&
          t.completedAt === null,
      ).length === 0)

  useEffect(() => {
    posthog.capture('today_view_opened')
  }, [])

  useEffect(() => {
    if (allDoneRef.current === null) {
      allDoneRef.current = earlyAllDone
      return
    }
    if (earlyAllDone && !allDoneRef.current) {
      posthog.capture('all_tasks_completed')
    }
    allDoneRef.current = earlyAllDone
  }, [earlyAllDone])

  // Habit completion — captured with the habit name (habits have no completion
  // event otherwise).
  function handleCompleteHabit(id: Id<'nodes'>) {
    const sid = id as string
    if (localDone.has(sid)) return
    // A fast isn't ticked off: the swipe / check starts it, or ends a running one.
    if (fast.isFastHabit(sid)) return void fast.toggle(id)
    const habit = dueHabits.find((h) => (h._id as string) === sid)
    posthog.capture('habit_completed', { habitName: habit?.title })
    // Mirror the task swipe: hold the done state, then slide + collapse, then drop.
    // Without this the optimistic completion filters the habit out instantly and
    // the row vanishes with no animation.
    setLocalDone((prev) => new Set([...prev, sid]))
    playDoneSound()
    completeHabit({ taskId: id })
    setTimeout(() => {
      setDismissing((prev) => new Set([...prev, sid]))
      setTimeout(() => {
        setDismissed((prev) => new Set([...prev, sid]))
      }, 300)
    }, 400)
    undoToast(t('today.markedDone', { defaultValue: 'Marked done' }), () => {
      const remove = (s: Set<string>) => {
        const n = new Set(s)
        n.delete(sid)
        return n
      }
      setLocalDone(remove)
      setDismissing(remove)
      setDismissed(remove)
      uncompleteHabit({ taskId: id }).catch(() => {})
    })
  }

  function handleComplete(taskId: Id<'nodes'>) {
    const id = taskId as string
    if (localDone.has(id)) return
    if (fast.isFastHabit(id)) return void fast.toggle(taskId)
    setLocalDone((prev) => new Set([...prev, id]))
    playDoneSound()
    const taskData =
      state.status === 'tasks' ? state.tasks.find((t) => (t.taskId as string) === id) : undefined
    if (startHereGoal && taskData?.goalId === startHereGoal) {
      clearStartHere()
      setStartHereGoal(null)
    }
    posthog.capture('task_completed', {
      effort: taskData?.effort,
      goalCategory: taskData?.category,
      taskName: taskData?.title,
      goalName: taskData?.goalTitle,
    })
    noteWin()
    if (online) {
      complete({ taskId }).catch(() => {
        toast.error("Couldn't save — please try again.")
        setLocalDone((prev) => {
          const n = new Set(prev)
          n.delete(id)
          return n
        })
      })
    } else {
      // Queue for replay on reconnect; the cached-read projection keeps it done.
      enqueueOp({ kind: 'complete', args: { taskId: id } })
    }
    setTimeout(() => {
      setDismissing((prev) => new Set([...prev, id]))
      setTimeout(() => {
        setDismissed((prev) => new Set([...prev, id]))
      }, 300)
    }, 400)
    undoToast(t('today.markedDone', { defaultValue: 'Marked done' }), () => {
      const remove = (s: Set<string>) => {
        const n = new Set(s)
        n.delete(id)
        return n
      }
      setLocalDone(remove)
      setDismissing(remove)
      setDismissed(remove)
      uncompleteTaskMut({ taskId }).catch(() => {})
    })
  }

  // Skip a surfaced task for today ("not today"): drops off Today, stays in its
  // goal, resurfaces later. Reason is optional and never required.
  function handleSkip(taskId: Id<'nodes'>, reason?: string) {
    const id = taskId as string
    if (dismissed.has(id)) return
    // Skipping a committed task defers it a day; it keeps its put-off count.
    if (state.status === 'tasks' && state.tasks.some((t) => t.taskId === taskId && t.mustDo)) {
      void mustDo(taskId, 'tomorrow')
      return
    }
    // Mark as skipping so the card exits left (mirrors the swipe-to-skip direction).
    setSkippingIds((prev) => new Set([...prev, id]))
    const localDate = todayKey()
    const skippedTask =
      state.status === 'tasks' ? state.tasks.find((t) => (t.taskId as string) === id) : undefined
    posthog.capture('task_skipped', {
      reason: reason ?? null,
      taskName: skippedTask?.title,
      goalName: skippedTask?.goalTitle,
    })
    if (online) {
      skipTodayMut({ taskId, localDate, ...(reason ? { reason } : {}) }).catch(() => {
        toast.error(t('common.saveFailed'))
      })
    } else {
      enqueueOp({
        kind: 'skipToday',
        args: { taskId: id, localDate, ...(reason ? { reason } : {}) },
      })
    }
    // Swipe/tap has no reason — offer reason chips + an Undo (web parity).
    if (!reason) {
      offerSkipReason({
        title: skippedTask?.title ?? '',
        reasons: TASK_SKIP_REASONS,
        onReason: (r) => {
          if (online) skipTodayMut({ taskId, localDate, reason: r }).catch(() => {})
          else enqueueOp({ kind: 'skipToday', args: { taskId: id, localDate, reason: r } })
        },
        onUndo: () => {
          const remove = (s: Set<string>) => {
            const n = new Set(s)
            n.delete(id)
            return n
          }
          setSkippingIds(remove)
          setDismissing(remove)
          setDismissed(remove)
          if (online) unskipTodayMut({ taskId, localDate }).catch(() => {})
          else enqueueOp({ kind: 'unskipToday', args: { taskId: id, localDate } })
        },
      })
    }
    setTimeout(() => {
      setDismissing((prev) => new Set([...prev, id]))
      setTimeout(() => {
        setDismissed((prev) => new Set([...prev, id]))
      }, 300)
    }, 400)
  }

  // Skip a habit for today: hides it from Today only (still completable from the
  // inbox Habits tab later, streak untouched). Reason optional, never required.
  function handleSkipHabit(habitId: Id<'nodes'>, reason?: string, picked = false) {
    const id = habitId as string
    if (dismissed.has(id)) return
    setSkippingIds((prev) => new Set([...prev, id]))
    const skippedHabit = dueHabits.find((h) => (h._id as string) === id)
    posthog.capture('habit_skipped', { reason: reason ?? null, habitName: skippedHabit?.title })
    if (online) {
      skipHabitTodayMut({ habitId, localDate: todayStr, ...(reason ? { reason } : {}) }).catch(() =>
        toast.error(t('common.saveFailed')),
      )
    } else {
      enqueueOp({
        kind: 'skipHabitToday',
        args: { habitId: id, localDate: todayStr, ...(reason ? { reason } : {}) },
      })
    }
    const undo = () => {
      const remove = (s: Set<string>) => {
        const n = new Set(s)
        n.delete(id)
        return n
      }
      setSkippingIds(remove)
      setDismissing(remove)
      setDismissed(remove)
      if (online) unskipHabitTodayMut({ habitId, localDate: todayStr }).catch(() => {})
      else enqueueOp({ kind: 'unskipHabitToday', args: { habitId: id, localDate: todayStr } })
    }
    // Reason picked up front (skip sheet) → plain undo toast. A bare swipe/shortcut
    // skip offers the optional reason afterwards; the detail sheet needs neither.
    if (picked) undoToast(t('today.skippedTitle', { title: skippedHabit?.title ?? '' }), undo)
    else if (!reason)
      offerSkipReason({
        title: skippedHabit?.title ?? '',
        reasons: HABIT_SKIP_REASONS,
        onReason: (r) => {
          if (online) skipHabitTodayMut({ habitId, localDate: todayStr, reason: r }).catch(() => {})
          else
            enqueueOp({
              kind: 'skipHabitToday',
              args: { habitId: id, localDate: todayStr, reason: r },
            })
        },
        onUndo: undo,
      })
    setTimeout(() => {
      setDismissing((prev) => new Set([...prev, id]))
      setTimeout(() => {
        setDismissed((prev) => new Set([...prev, id]))
      }, 300)
    }, 400)
  }

  const habitIds = new Set((habitsData ?? []).map((h) => h._id as string))
  const pendingHabitNav = dueHabits
    .filter((h) => !h.completions.some((ms) => localDayStr(ms) === todayStr))
    .map((h) => habitToTodayTask(h, false))
  const surfaceNav =
    state.status === 'tasks'
      ? state.tasks
          .map((t) => ({ ...t, localDone: localDone.has(t.taskId as string) }))
          .filter(
            (t) =>
              !t.localDone &&
              t.completedAt === null &&
              !dismissed.has(t.taskId as string) &&
              !habitIds.has(t.taskId as string),
          )
      : []
  // Match the visual order: Focus tasks, then Habits, then Quick tasks.
  const navigableTasks = [
    ...surfaceNav.filter((t) => !t.isInbox).sort(byUrgentFirst),
    ...pendingHabitNav,
    ...surfaceNav.filter((t) => t.isInbox).sort(byUrgentFirst),
  ]

  kbStateRef.current = {
    navigable: navigableTasks,
    selectedTask,
    focusedTaskId,
    helpOpen,
    handleComplete,
    setSelectedTask,
    setSelectedHabitId,
    habitIds,
    setHelpOpen,
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const kb = kbStateRef.current
      // Still mounted (hidden) while the Friends or Groves tab is showing.
      if (!kb || new URLSearchParams(window.location.search).has('tab')) return
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
        return

      if (e.key === '?' || e.key === 'Escape') {
        if (kb.helpOpen) {
          e.preventDefault()
          kb.setHelpOpen(false)
        } else if (e.key === '?') {
          e.preventDefault()
          kb.setHelpOpen(true)
        }
        return
      }
      if (kb.selectedTask !== null || kb.helpOpen) return

      const nav = kb.navigable
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault()
        const idx = nav.findIndex((t) => (t.taskId as string) === kb.focusedTaskId)
        const next = nav[idx + 1] ?? nav[0]
        if (next) {
          setFocusedTaskId(next.taskId as string)
          document
            .querySelector(`[data-today-task-id="${next.taskId as string}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault()
        const idx = nav.findIndex((t) => (t.taskId as string) === kb.focusedTaskId)
        const prev = nav[idx - 1] ?? nav[nav.length - 1]
        if (prev) {
          setFocusedTaskId(prev.taskId as string)
          document
            .querySelector(`[data-today-task-id="${prev.taskId as string}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      } else if (e.key === ' ') {
        if (!kb.focusedTaskId) return
        e.preventDefault()
        const task = nav.find((t) => (t.taskId as string) === kb.focusedTaskId)
        if (task) kb.handleComplete(task.taskId)
      } else if (e.key === 'Enter') {
        if (!kb.focusedTaskId) return
        e.preventDefault()
        const task = nav.find((t) => (t.taskId as string) === kb.focusedTaskId)
        if (task) {
          if (kb.habitIds.has(task.taskId as string)) kb.setSelectedHabitId(task.taskId)
          else kb.setSelectedTask(task)
        }
      } else if (e.key === 'c') {
        if (!kb.focusedTaskId) return
        e.preventDefault()
        const task = nav.find((t) => (t.taskId as string) === kb.focusedTaskId)
        if (!task) return
        if (task.isInbox) {
          const title = taskTitle(task)
          const desc = taskDescription(task)
          copyWithToast(desc ? `${title}\n${desc}` : title)
        } else {
          copyWithToast(task.goalTitle ? `${task.title}\n${task.goalTitle}` : task.title)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Habits render in their own section; drop any that a stale surface still
  // carries so they don't also appear as a "quick" task.
  const habitIdSet = new Set((habitsData ?? []).map((h) => h._id as string))
  const tasks = (state.status === 'tasks' ? state.tasks : [])
    .filter((t) => !habitIdSet.has(t.taskId as string))
    .map((t) => ({
      ...t,
      localDone: localDone.has(t.taskId as string),
    }))
  const visibleTasks = tasks.filter((t) => !dismissed.has(t.taskId as string))
  // List view hides already-completed tasks; kanban keeps them in the Done column
  const listVisibleTasks =
    view === 'list'
      ? visibleTasks.filter((t) => t.localDone || t.completedAt === null)
      : visibleTasks
  const filteredVisible = listVisibleTasks
  const startHereTaskId = startHereGoal
    ? tasks.find((t) => t.goalId === startHereGoal && !t.localDone && t.completedAt === null)
        ?.taskId
    : undefined
  const pendingHabits = dueHabits.filter(
    (h) => !h.completions.some((ms) => localDayStr(ms) === todayStr),
  ).length
  const remaining =
    visibleTasks.filter((t) => !t.localDone && t.completedAt === null).length + pendingHabits
  const dayTotal = visibleTasks.length + dueHabits.length
  useHeaderSubtitle(
    !onTodayTab || breather.onBreather || state.status === 'loading' || habitsData === undefined
      ? undefined
      : remaining === 0 && (dayTotal > 0 || state.status === 'all-completed')
        ? t('headerSub.allDone')
        : remaining > 0
          ? t('headerSub.left', { left: remaining, total: dayTotal })
          : undefined,
  )

  // Hold the skeleton until habits are also ready (first load only — on revisit
  // they hydrate from cache) so tasks and habits appear together, no pop-in.
  const waitingForHabits =
    habitsData === undefined && (state.status === 'tasks' || state.status === 'all-completed')
  if (state.status === 'loading' || waitingForHabits) {
    const headerSkeleton = (
      <div className="mb-4">
        <div className="h-7 w-44 animate-pulse rounded-lg bg-muted/50" />
        <SkeletonProgressCard />
      </div>
    )
    if (view === 'kanban') {
      return (
        <div className="bg-background px-4 pt-4 pb-4">
          <div className="mx-auto max-w-(--page-col)">{headerSkeleton}</div>
          <TodayKanbanSkeleton />
        </div>
      )
    }
    return (
      <div className="bg-background px-4 pt-4 pb-4">
        <div className="mx-auto max-w-(--page-col)">
          {headerSkeleton}
          <TodayListSkeleton />
        </div>
      </div>
    )
  }

  // On a breather: replace the whole task list with a calm paused screen.
  if (breather.onBreather) {
    return <BreatherScreen until={breather.until} onEnd={() => breather.end({})} />
  }

  if (state.status === 'no-goals') {
    return (
      <div className="flex h-full items-center justify-center">
        <GoalsEmptyState />
      </div>
    )
  }

  if (state.status === 'no-surface') {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-background px-4 text-center">
        <p className="text-xl font-bold text-foreground">{t('today.empty')}</p>
        <p className="mt-2 text-sm text-muted-foreground">{t('today.emptyDesc')}</p>
        <BrainDumpEmptyAction className="mt-4" />
      </div>
    )
  }

  const allDone = (state.status === 'all-completed' || remaining === 0) && pendingHabits === 0
  const allDoneView = allDone && visibleTasks.length === 0 && !todayCompact
  // Nothing was planned and nothing done (e.g. a new account with only its Inbox): not "all done".
  const emptyDay =
    allDone &&
    visibleTasks.length === 0 &&
    !tasks.some((t) => t.localDone || t.completedAt !== null) &&
    !(habitsData ?? []).some((h) => h.completions.some((ms) => localDayStr(ms) === todayStr))
  const schedule = (
    <>
      <TodaySchedule busy={state.calendarBusy} />
      {!todayCompact && <ComingUpPanel limit={3} hideWhenEmpty className="mb-4 w-full" />}
    </>
  )

  // Compact summary sections (tasks / habits / goals). Rendered in place of the
  // list when the compact toggle is on; the header + filter bar stay so the
  // toggle (which lives in the filter bar) is reachable from both modes.
  const compactSections = (() => {
    if (!todayCompact) return null
    const compactTasks = visibleTasks.filter(
      (tk) => !tk.localDone && tk.completedAt === null && (tk.isInbox ? showQuick : showFocus),
    )
    const stillDue = dueHabits.filter(
      (h) => !h.completions.some((ms) => localDayStr(ms) === todayStr),
    )
    const doneToday = (habitsData ?? []).filter((h) =>
      h.completions.some((ms) => localDayStr(ms) === todayStr),
    )
    const compactGoals = (goalsTree ?? [])
      .map((g) => {
        let left = 0
        for (const ms of g.milestones)
          for (const tg of ms.targets)
            left += tg.tasks.filter((tk) => tk.completedAt === null).length
        return {
          id: g._id as string,
          title: g.title,
          pct: Math.round(Math.min(100, Math.max(0, g.progress))),
          left,
        }
      })
      .sort((a, b) => b.pct - a.pct)

    const rowClass =
      'flex items-center gap-2 py-1 -mx-1 px-1 min-w-0 cursor-pointer rounded-md hover:bg-muted/40'
    // Name gets priority; meta shrinks (and ellipsizes) first under space pressure.
    const lineClassTight = 'flex flex-1 min-w-0 items-baseline'
    const nameClass = 'min-w-0 truncate text-sm text-foreground'
    const metaClass = 'min-w-0 shrink-[4] truncate text-sm text-muted-foreground'
    // Fixed meta never truncates — the name yields first instead.
    const metaFixedClass = 'shrink-0 whitespace-nowrap text-sm text-muted-foreground'

    // Compact merges Focus + Quick into one Tasks block; Habits goes above it when
    // ordered above both task sections.
    const habitsFirst =
      sectionOrder.indexOf('habits') <
      Math.min(sectionOrder.indexOf('focus'), sectionOrder.indexOf('quick'))

    return (
      <div className="flex flex-col">
        <section className="mb-7" style={{ order: habitsFirst ? 2 : 1 }}>
          <TodaySectionHeader
            label={t('inbox.tabTasks')}
            count={t('todaySchedule.left', { count: compactTasks.length })}
          />
          {compactTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('today.compactNoTasks')}</p>
          ) : (
            <ul>
              {compactTasks.map((task) => (
                <li
                  key={task.taskId as string}
                  className={rowClass}
                  onClick={() => setSelectedTask(task)}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleComplete(task.taskId)
                    }}
                    aria-label={t('today.markComplete')}
                    className="shrink-0 cursor-pointer text-muted-foreground transition-colors hover:text-primary-text"
                  >
                    <TaskStatusIcon status="pending" size={16} />
                  </button>
                  <span className="flex flex-1 min-w-0 items-baseline">
                    <span className={nameClass}>{task.title}</span>
                    {task.effort === 'high' && (
                      <span className="ml-1.5 shrink-0 whitespace-nowrap text-sm text-muted-foreground">
                        (high)
                      </span>
                    )}
                    {!task.isInbox && task.goalTitle && (
                      <span
                        className={cn(
                          'min-w-0 shrink truncate text-sm text-muted-foreground',
                          task.effort !== 'high' && 'ml-1.5',
                        )}
                      >
                        ・{task.goalTitle}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {showHabits && (
          <section className="mb-7" style={{ order: habitsFirst ? 1 : 2 }}>
            <TodaySectionHeader
              label={t('today.sectionHabits')}
              count={`${doneToday.length}/${stillDue.length + doneToday.length}`}
            />
            {stillDue.length > 0 && (
              <>
                <p className="mt-1 mb-1 text-xs font-medium text-muted-foreground">
                  {t('today.compactStillDue')}
                </p>
                <ul>
                  {stillDue.map((habit) => {
                    const streak = habitDayStreak(
                      habit.completions,
                      habit.recursPeriod,
                      habit.recursDays,
                    )
                    const doneDays = new Set(habit.completions.map((ms) => localDayStr(ms)))
                    const dotColor = fill(habitColor(habit.category))
                    const recentDots = Array.from({ length: 7 }, (_, i) => {
                      const d = new Date()
                      d.setDate(d.getDate() - (6 - i))
                      return doneDays.has(localDayStr(d.getTime()))
                    })
                    return (
                      <li
                        key={habit._id as string}
                        className={rowClass}
                        onClick={() => setSelectedHabitId(habit._id)}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCompleteHabit(habit._id)
                          }}
                          aria-label={t('today.markComplete')}
                          className="shrink-0 cursor-pointer text-muted-foreground transition-colors hover:text-primary-text"
                        >
                          <TaskStatusIcon status="pending" size={16} />
                        </button>
                        <span className={lineClassTight}>
                          <span className={nameClass}>{habit.title}</span>
                          {(habit.recurrenceTime || streak > 0 || !!habit.strength) && (
                            <span className={metaClass}>
                              ・{habit.recurrenceTime}
                              {habit.recurrenceTime && streak > 0 && ' · '}
                              {streak > 0 && (
                                <StreakShare habitId={habit._id} streak={streak}>
                                  {streak}
                                  <StreakFlame />
                                </StreakShare>
                              )}
                              {!!habit.strength && (
                                <>
                                  {(habit.recurrenceTime || streak > 0) && ' · '}
                                  <StrengthChip value={habit.strength} />
                                </>
                              )}
                            </span>
                          )}
                        </span>
                        <span className="flex shrink-0 items-center gap-0.5">
                          {recentDots.map((done, i) => (
                            <span
                              key={i}
                              className={cn(
                                'h-1.5 w-1.5 rounded-full',
                                !done && 'bg-muted-foreground/20',
                              )}
                              style={done ? { backgroundColor: dotColor } : undefined}
                            />
                          ))}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </>
            )}
            {doneToday.length > 0 && (
              <p className="mt-2 truncate text-sm text-muted-foreground">
                <span className="font-medium">{t('today.compactAlreadyDone')}</span>{' '}
                {doneToday.map((h) => h.title).join(', ')}
              </p>
            )}
            {stillDue.length === 0 && doneToday.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('today.compactNoHabits')}</p>
            )}
            <TodayLevelUp className="mt-3" />
          </section>
        )}

        <section style={{ order: 3 }}>
          <TodaySectionHeader label={t('nav.goals')} />
          {compactGoals.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {habitsFocus ? t('goals.bigger') : t('today.compactNoGoals')}{' '}
              <Link
                to="/goals/new"
                search={{ goal: undefined }}
                className={
                  habitsFocus
                    ? 'font-medium underline underline-offset-4 hover:text-foreground'
                    : 'font-semibold text-primary-text underline-offset-4 hover:underline'
                }
              >
                {habitsFocus ? t('goals.startGoal') : t('goals.createFirst')}
              </Link>
            </p>
          ) : (
            <ul>
              {compactGoals.map((goal) => (
                <li
                  key={goal.id}
                  className={rowClass}
                  onClick={() => navigate({ to: '/goals/$goalId', params: { goalId: goal.id } })}
                >
                  <span className="w-4 shrink-0 text-center text-muted-foreground/40">-</span>
                  <span className={lineClassTight}>
                    <span className={nameClass}>{goal.title}</span>
                    <span className={metaFixedClass}>
                      ・{goal.pct}% ({t('today.compactLeft', { n: goal.left })})
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    )
  })()

  const header = (
    <div className="mb-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-foreground">
          {emptyDay
            ? t('today.empty')
            : allDoneView
              ? t('today.recap.title')
              : allDone
                ? t('today.allDone')
                : startHereGoal && onboardedToday
                  ? t('today.dayOne')
                  : t('today.tasksRemaining', { count: remaining })}
        </h2>
        <div className="flex shrink-0 items-center gap-3">
          <TrialChip />
          {streak > 0 && allDoneView && (
            <span
              className="text-sm font-bold text-foreground tabular-nums"
              title={`${streak} ${t('today.streakDays')}`}
            >
              <StreakFlame /> {streak}
            </span>
          )}
          {view === 'list' && (
            <DensityToggle
              compact={todayCompact}
              onToggle={toggleTodayCompact}
              className="self-center"
            />
          )}
        </div>
      </div>
      {frozeLastNight && <p className="mt-1 text-xs text-blue-400">{t('today.streakFroze')}</p>}
      {todayCompact ? (
        <TodayProgressLine
          className="mt-1"
          done={visibleTasks.length + dueHabits.length - remaining}
          total={visibleTasks.length + dueHabits.length}
          streak={streak}
        />
      ) : (
        !allDoneView &&
        !(startHereGoal && onboardedToday) && (
          <TodayProgressCard
            className="mt-3"
            done={visibleTasks.length + dueHabits.length - remaining}
            total={visibleTasks.length + dueHabits.length}
            habitsDone={dueHabits.length - pendingHabits}
            habitsDue={dueHabits.length}
            streak={streak}
            freezes={streakFreezes}
          />
        )
      )}
      {!todayCompact && (
        <>
          <CoachTonePrompt className="mt-3" />
          <GettingStartedCard className="mt-3" />
          <GroveToday className="mt-3" />
        </>
      )}
      {streak > 0 && !allDoneView && !todayCompact && (
        <div className="mt-1.5">
          <RotatingTip
            tipsKey="today.tips"
            desktopTipsKey="today.tipsDesktop"
            values={{
              brainDump: captureFieldShortcutLabel(),
              search: isMacPlatform() ? '⌘K' : 'Ctrl+K',
            }}
          />
        </div>
      )}
      <StreakStoryPrompt />
    </div>
  )

  if (allDoneView) {
    return (
      <div className="bg-background px-4 pt-4 pb-24">
        <div className="mx-auto max-w-(--page-col)">
          {header}
          {schedule}
          {emptyDay && !hasRealGoal ? (
            <GoalsEmptyState />
          ) : (
            <TodayAllDone localDate={todayStr} streak={streak} emptyDay={emptyDay} />
          )}
          <BrainDumpEmptyAction className="mt-4" />
        </div>
      </div>
    )
  }

  if (view === 'kanban') {
    return (
      <>
        <div className="bg-background px-4 pt-4 pb-4">
          {/* Only the board goes full width; everything above stays in the list column. */}
          <div className="mx-auto max-w-(--page-col)">
            {header}
            {schedule}
            <CoachNoteCard className="mb-3" />
            <WeeklyRecapCard className="mb-3" />
            <HabitSuggestionCard className="mb-3" />
          </div>
          <TodayKanban
            tasks={[
              ...filteredVisible,
              ...dueHabits.map((h) =>
                habitToTodayTask(
                  h,
                  h.completions.some((ms) => localDayStr(ms) === todayStr),
                ),
              ),
            ]}
            onDetail={(tk) =>
              habitIdSet.has(tk.taskId as string)
                ? setSelectedHabitId(tk.taskId)
                : setSelectedTask(tk)
            }
          />
          {allDone && tasks.length > 0 && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => state.resurface()}
                className="cursor-pointer rounded-full border border-border px-4 py-2.5 text-sm font-medium text-foreground/60 hover:border-foreground/20 hover:text-foreground transition-colors"
              >
                {t('today.getMoreTasks')}
              </button>
            </div>
          )}
        </div>
        <TaskDetailSheet
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
        <InboxTaskDetailSheet
          task={habitForSheet}
          onClose={() => setSelectedHabitId(null)}
          onComplete={handleCompleteHabit}
          onUncomplete={(id) => uncompleteHabit({ taskId: id })}
        />
      </>
    )
  }

  const activeNudge =
    state.status === 'tasks' || state.status === 'all-completed' ? state.activeNudge : null
  const calendarDayLoad =
    state.status === 'tasks' || state.status === 'all-completed' ? state.calendarDayLoad : null

  return (
    <>
      <div className="bg-background px-4 pt-4 pb-4">
        <div className="mx-auto max-w-(--page-col)">
          {header}
          {schedule}
          {showHabits && (
            <YesterdayCatchUp habits={missedYesterday} collapsed={todayCompact} className="mb-3" />
          )}
          {!todayCompact && (
            <>
              {!locationDismissed && existingLocation === null && (
                <LocationSetupCard
                  onSave={(city, country, lat, lon) => {
                    void storeLocation({ city, country, latitude: lat, longitude: lon })
                  }}
                  onDismiss={() => {
                    dismissLocationCard()
                    fetch('https://api.goaltree.ai/location')
                      .then((r) => r.json())
                      .then(
                        (data: {
                          city?: string | null
                          country?: string | null
                          lat?: string | null
                          lon?: string | null
                        }) => {
                          const latitude = data.lat ? parseFloat(data.lat) : null
                          const longitude = data.lon ? parseFloat(data.lon) : null
                          if (
                            data.city &&
                            latitude != null &&
                            longitude != null &&
                            !isNaN(latitude) &&
                            !isNaN(longitude)
                          ) {
                            void storeLocation({
                              city: data.city,
                              country: data.country ?? '',
                              latitude,
                              longitude,
                            })
                          }
                        },
                      )
                      .catch(() => {})
                  }}
                />
              )}
              {!calendarCardDismissed && calendarConnections?.length === 0 && (
                <CalendarSetupCard
                  onConnect={() => navigate({ to: '/settings' })}
                  onDismiss={dismissCalendarCard}
                />
              )}
              {calendarDayLoad && calendarDayLoad !== 'light' && (
                <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                  <CalendarDays className="size-3.5" />
                  <span>{t(`today.calendarLoad.${calendarDayLoad}`)}</span>
                </div>
              )}
              <CoachNoteCard className="mb-3" />
              <PromoBanner className="mb-3" />
              {breather.returnPending && (
                <div className="mb-3">
                  <WelcomeBackCard onDismiss={() => breather.dismissReturn({})} />
                </div>
              )}
              <WeeklyRecapCard className="mb-3" />
              {showReviewPrompt && (
                <button
                  type="button"
                  onClick={() => navigate({ to: '/review' })}
                  className="cursor-pointer mb-3 w-full flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3.5 text-left transition-colors hover:bg-primary/10"
                >
                  <ClipboardList className="size-5 shrink-0 text-primary-text" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                      {t('review.promptTitle')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('review.promptDesc')}</p>
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              )}
              {activeNudge && <CoachCard message={activeNudge} onDismiss={() => dismissNudge()} />}
              <HabitSuggestionCard className="mb-3" />
            </>
          )}
          {todayCompact ? (
            compactSections
          ) : (
            <LegendList
              data={(() => {
                // Completed habits drop off Today (like tasks); toggle them back
                // from the Habits tab.
                const pendingHabitList = dueHabits.filter((h) => {
                  const hid = h._id as string
                  if (dismissed.has(hid)) return false
                  // Keep a just-completed habit on screen through its dismiss animation
                  // even though the optimistic completion already marked it done today.
                  if (localDone.has(hid)) return true
                  return !h.completions.some((ms) => localDayStr(ms) === todayStr)
                })
                const habitRows: TodayListRow[] =
                  showHabits && pendingHabitList.length
                    ? [
                        {
                          kind: 'section',
                          label: t('today.sectionHabits'),
                          count: `${dueHabits.length - pendingHabits}/${dueHabits.length}`,
                        },
                        ...pendingHabitList.map(
                          (habit, i) =>
                            ({ kind: 'habit', habit, done: false, first: i === 0 }) as TodayListRow,
                        ),
                      ]
                    : []

                const mustTasks = filteredVisible.filter((t) => t.mustDo)
                const autoTasks = filteredVisible.filter((t) => !t.mustDo)
                const focusTasks = autoTasks.filter((t) => !t.isInbox).sort(byUrgentFirst)
                const quickTasks = autoTasks.filter((t) => t.isInbox).sort(byUrgentFirst)
                const leftCount = (list: typeof focusTasks) =>
                  t('todaySchedule.left', { count: list.filter((tk) => !tk.localDone).length })
                const rows: TodayListRow[] = []
                if (mustTasks.length) {
                  rows.push({ kind: 'mustHead', count: mustTasks.length })
                  mustTasks.forEach((task, i) =>
                    rows.push({
                      kind: 'task',
                      task,
                      taskIndex: i,
                      must:
                        mustTasks.length === 1
                          ? 'only'
                          : i === 0
                            ? 'first'
                            : i === mustTasks.length - 1
                              ? 'last'
                              : 'mid',
                    }),
                  )
                  const stuck = mustTasks.find(
                    (t) =>
                      t.putOffDays >= NUDGE_AFTER_DAYS &&
                      !t.localDone &&
                      !nudgeDismissed(t.taskId as string) &&
                      !nudgeHidden.has(t.taskId as string),
                  )
                  if (stuck) rows.push({ kind: 'nudge', task: stuck })
                }
                for (const section of sectionOrder) {
                  if (section === 'focus' && showFocus && focusTasks.length > 0) {
                    rows.push({
                      kind: 'section',
                      label: t('today.sectionFocus'),
                      count: leftCount(focusTasks),
                    })
                    focusTasks.forEach((task, i) => rows.push({ kind: 'task', task, taskIndex: i }))
                  } else if (section === 'habits' && showHabits) {
                    rows.push(...habitRows, { kind: 'levelUp' })
                  } else if (section === 'quick' && showQuick && quickTasks.length > 0) {
                    rows.push({
                      kind: 'section',
                      label: t('today.sectionQuick'),
                      count: leftCount(quickTasks),
                    })
                    quickTasks.forEach((task, i) =>
                      rows.push({ kind: 'task', task, taskIndex: focusTasks.length + i }),
                    )
                  }
                }
                if (queued.length) rows.push({ kind: 'tray', queued })
                return rows
              })()}
              keyExtractor={(row) =>
                row.kind === 'task'
                  ? (row.task.taskId as string)
                  : row.kind === 'habit'
                    ? `habit-${row.habit._id as string}`
                    : row.kind === 'section'
                      ? `section-${row.label}`
                      : row.kind === 'mustHead'
                        ? 'must-head'
                        : row.kind === 'nudge'
                          ? `nudge-${row.task.taskId as string}`
                          : row.kind === 'tray'
                            ? 'tray'
                            : row.kind === 'levelUp'
                              ? 'level-up'
                              : 'divider'
              }
              renderItem={({ item: row }) => {
                if (row.kind === 'divider') {
                  return <div className="my-4 h-px bg-border/50" />
                }
                if (row.kind === 'levelUp') return <TodayLevelUp className="mt-2 mb-1" />
                if (row.kind === 'mustHead') {
                  return <MustDoHeader count={row.count} limit={MUST_DO_LIMIT} />
                }
                if (row.kind === 'nudge') {
                  return (
                    <MustDoNudge
                      task={row.task}
                      onPickTime={() => {
                        const full = filteredVisible.find((t) => t.taskId === row.task.taskId)
                        if (full) setSelectedTask(full)
                      }}
                      onDismiss={() =>
                        setNudgeHidden((prev) => new Set([...prev, row.task.taskId as string]))
                      }
                    />
                  )
                }
                if (row.kind === 'tray') {
                  return (
                    <QueuedTray
                      queued={row.queued}
                      onOpen={(id) => {
                        const full = tasks.find((t) => t.taskId === id)
                        if (full) setSelectedTask(full)
                      }}
                    />
                  )
                }
                if (row.kind === 'section') {
                  return <TodaySectionHeader label={row.label} count={row.count} />
                }
                if (row.kind === 'habit') {
                  const habitId = row.habit._id
                  // Plain div (no motion.div): framer-motion's layout re-measures
                  // as the list streams in and restarts the dot-grid CSS animation.
                  return (
                    <div>
                      <TaskCard
                        task={habitToTodayTask(row.habit, localDone.has(habitId as string))}
                        index={0}
                        onComplete={handleCompleteHabit}
                        onSkip={handleSkipHabit}
                        isDismissing={dismissing.has(habitId as string)}
                        isSkipping={skippingIds.has(habitId as string)}
                        onPress={() => setSelectedHabitId(habitId)}
                        habitCompletions={row.habit.completions}
                        focused={focusedTaskId === (habitId as string)}
                        flameTip={row.first}
                        habitCreatedAt={row.habit._creationTime}
                      />
                    </div>
                  )
                }
                const card = (
                  <TaskCard
                    task={row.task}
                    index={row.taskIndex}
                    onComplete={handleComplete}
                    onSkip={handleSkip}
                    isDismissing={dismissing.has(row.task.taskId as string)}
                    isSkipping={skippingIds.has(row.task.taskId as string)}
                    onPress={setSelectedTask}
                    likeCount={likeCountMap.get(row.task.taskId as string) ?? 0}
                    focused={focusedTaskId === (row.task.taskId as string)}
                    startHere={row.task.taskId === startHereTaskId}
                    divider={
                      !row.must
                        ? 'default'
                        : row.must === 'last' || row.must === 'only'
                          ? 'none'
                          : 'lane'
                    }
                  />
                )
                return (
                  <motion.div layout transition={{ duration: 0.2, ease: 'easeOut' }}>
                    {row.must ? (
                      <MustDoRow position={row.must} putOffDays={row.task.putOffDays}>
                        {card}
                      </MustDoRow>
                    ) : (
                      card
                    )}
                  </motion.div>
                )
              }}
              recycleItems={false}
              estimatedItemSize={88}
              style={{ flex: 1, overflow: 'visible' }}
            />
          )}
          {!todayCompact && <TrajectoryTeaser className="mt-4" />}
          <JourneyPromptCard className="mt-4" />
        </div>
      </div>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground/60">
              Keyboard shortcuts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 font-mono text-sm">
            {(
              [
                ['J / ↓', 'Next task'],
                ['K / ↑', 'Previous task'],
                ['Space', 'Complete task'],
                ['Enter', 'Open details'],
                ['C', 'Copy title & goal'],
                ['B / ⌘B', 'Brain dump'],
                ['T', 'New task'],
                ['H', 'New habit'],
                ['G', 'New goal'],
                ['?', 'Show shortcuts'],
                ['Esc', 'Close'],
              ] as const
            ).map(([key, label]) => (
              <div
                key={key}
                className="flex items-center justify-between gap-4 border-b border-border/30 py-1.5 last:border-0"
              >
                <span className="text-xs text-muted-foreground">{label}</span>
                <kbd className="rounded border border-border/60 bg-muted/50 px-2 py-0.5 font-mono text-xs text-foreground/70">
                  {key}
                </kbd>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <TaskDetailSheet
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        onComplete={handleComplete}
        onSkip={handleSkip}
      />
      <InboxTaskDetailSheet
        task={habitForSheet}
        onClose={() => setSelectedHabitId(null)}
        onComplete={handleCompleteHabit}
        onUncomplete={(id) => uncompleteHabit({ taskId: id })}
        onSkipToday={(id, reason) => {
          handleSkipHabit(id, reason)
          setSelectedHabitId(null)
        }}
      />
    </>
  )
}

// Memoised: the layout re-renders on every tab switch, which shouldn't re-render
// the kept (hidden) tabs' whole screens.
const KeptToday = memo(TodayContent)
const KeptFeed = memo(MomentumFeed)
const KeptLive = memo(LiveStrip)
const KeptGroves = memo(GrovesList)

function TodayViewToggle({ className }: { className?: string }) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const rawView = useViewStore((s) => s.view)
  const view: 'list' | 'kanban' = !isMobile && rawView === 'kanban' ? 'kanban' : 'list'
  const setView = useViewStore((s) => s.setView)
  const todayCompact = useViewStore((s) => s.todayCompact)
  const toggleTodayCompact = useViewStore((s) => s.toggleTodayCompact)
  return (
    <div
      className={cn(
        'flex items-center divide-x divide-border overflow-hidden rounded-md border border-border',
        className,
      )}
    >
      {(
        [
          {
            key: 'list',
            icon: <LayoutList className="h-3.5 w-3.5" />,
            label: t('goals.viewList'),
            active: view === 'list',
            onClick: () => setView('list'),
          },
          {
            key: 'kanban',
            icon: <Columns3 className="h-3.5 w-3.5" />,
            label: t('goals.viewKanban'),
            active: view === 'kanban',
            onClick: () => {
              setView('kanban')
              if (todayCompact) toggleTodayCompact()
            },
          },
        ] as const
      ).map(({ key, icon, label, active, onClick }) => (
        <IconTip key={key} label={label}>
          <button
            type="button"
            aria-label={label}
            onClick={onClick}
            className={cn(
              'cursor-pointer flex h-8 w-8 items-center justify-center transition-colors',
              active
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {icon}
          </button>
        </IconTip>
      ))}
    </div>
  )
}

function RouteComponent() {
  const { t } = useTranslation()
  useMarkNotifsReadOnView('dailySurface')
  const search = Route.useSearch()
  const tab: TodayTab = search.tab ?? 'today'
  const openGrove = search.grove
  const navigate = useNavigate({ from: '/today' })
  const setTab = (tk: TodayTab) =>
    navigate({
      search: { ...(tk === 'today' ? {} : { tab: tk }), grove: openGrove },
      replace: true,
      resetScroll: false,
    })
  const clearStoryLink = useCallback(
    () =>
      navigate({
        search: (s) => ({ ...s, story: undefined }),
        replace: true,
        resetScroll: false,
      }),
    [navigate],
  )
  const title = t(TODAY_TAB_LABEL[tab])
  const { onScroll } = useScrollTitle(title)
  const setScrolled = useHeaderStore((s) => s.setScrolled)
  const setRaised = useHeaderStore((s) => s.setRaised)
  const scrollers = useRef<Partial<Record<TodayTab, HTMLDivElement | null>>>({})
  useEffect(() => {
    const y = scrollers.current[tab]?.scrollTop ?? 0
    setScrolled(y > 80)
    setRaised(y > 0)
  }, [tab, setScrolled, setRaised])
  const scrollerProps = (tk: TodayTab) => ({
    ref: (el: HTMLDivElement | null) => {
      scrollers.current[tk] = el
    },
    onScroll: tk === tab ? onScroll : undefined,
    'data-scroll-reset': tk === tab ? '' : undefined,
    'data-pane-scroll': '',
    style: { paddingTop: 'var(--app-hdr-h)' },
    // Each tab keeps its own saved position (they share the /today pathname key).
    'data-scroll-restoration-id': tk === 'today' ? 'tab-scroll' : `today-${tk}-scroll`,
  })
  const rawView = useViewStore((s) => s.view)
  const isMobile = useIsMobile()
  const view: 'list' | 'kanban' = !isMobile && rawView === 'kanban' ? 'kanban' : 'list'
  const todayCompact = useViewStore((s) => s.todayCompact)
  const pushNudgeDismissed = useViewStore((s) => s.pushNudgeDismissed)
  const dismissPushNudge = useViewStore((s) => s.dismissPushNudge)
  const {
    supported: pushSupported,
    permission: pushPermission,
    isSubscribed,
    subscribe: subscribePush,
  } = useWebPush()
  // iPhone Safari has no web push until Goaltree is on the Home Screen.
  const canAskInstall = useCanAskInstall()
  const iosInstallNudge = !pushSupported && installPlatform() === 'ios' && canAskInstall

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background relative">
        <MobileScreenHeader
          title={title}
          showInbox={false}
          leftActions={<ShareDayButton />}
          rightSlot={
            <div className="flex items-center gap-1">
              <CoachHeaderButton />
              <IconTip label={t('nav.search')}>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('open-search'))}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={t('nav.search')}
                >
                  <SearchIcon className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </IconTip>
            </div>
          }
        />
        <SiteHeader
          rightSlot={
            <>
              {tab === 'today' && <TodayViewToggle />}
              <BrainDumpHeaderButton />
              <ShareDayButton />
              <TodayHeaderMenu />
            </>
          }
          className="hidden lg:flex"
        />
        <DayShareDeepLink />
        <TodayTabs
          tab={tab}
          onTab={setTab}
          panes={{
            today: (header) => (
              <>
                <div
                  className="flex-1 overflow-y-auto pb-32 lg:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  {...scrollerProps('today')}
                >
                  {header}
                  {!todayCompact &&
                    !pushNudgeDismissed &&
                    pushSupported &&
                    pushPermission === 'default' &&
                    !isSubscribed && (
                      <div className="shrink-0 px-4 pt-3 pb-0">
                        <div className="mx-auto max-w-(--page-col)">
                          <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-muted/30 px-4 py-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                              <BellIcon className="h-4 w-4 text-primary-text" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground">
                                {t('today.pushNudgeTitle')}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {t('today.pushNudgeDesc')}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <button
                                type="button"
                                onClick={async () => {
                                  const ok = await subscribePush()
                                  if (ok) dismissPushNudge()
                                }}
                                className="cursor-pointer rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-white dark:text-[#0a0a0a] transition-opacity hover:opacity-90"
                              >
                                {t('today.pushNudgeCta')}
                              </button>
                              <button
                                type="button"
                                onClick={dismissPushNudge}
                                className="cursor-pointer flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                              >
                                <XClose className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  {!todayCompact && iosInstallNudge && (
                    <div className="shrink-0 px-4 pt-3 pb-0">
                      <div className="mx-auto max-w-(--page-col)">
                        <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-muted/30 px-4 py-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                            <BellIcon className="h-4 w-4 text-primary-text" strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-foreground">
                              {t('pwa.nudgeTitle')}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {t('pwa.nudgeDesc')}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              type="button"
                              onClick={() => requestInstall('reminders')}
                              className="cursor-pointer rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-white transition-opacity hover:opacity-90 dark:text-[#0a0a0a]"
                            >
                              {t('pwa.nudgeCta')}
                            </button>
                            <button
                              type="button"
                              onClick={snoozeInstall}
                              aria-label={t('pwa.maybeLater')}
                              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                              <XClose className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <KeptToday view={view} />
                </div>
              </>
            ),
            friends: (header) => (
              <div className="flex-1 overflow-y-auto pb-36 lg:pb-24" {...scrollerProps('friends')}>
                {header}
                <div className="px-4 pt-4 pb-6">
                  <div className="mx-auto max-w-(--page-col)">
                    <StoryRail linkItemId={search.story} onLinkHandled={clearStoryLink} />
                    <KeptLive />
                    <p className="mb-4 text-sm text-muted-foreground">{t('momentum.subtitle')}</p>
                    <KeptFeed />
                  </div>
                </div>
              </div>
            ),
            groves: (header) =>
              openGrove ? (
                <div className="flex min-h-0 flex-1 flex-col pt-(--app-hdr-h)">
                  {header}
                  <GroveInline
                    groveId={openGrove}
                    onBack={() =>
                      navigate({ search: { tab: 'groves' }, replace: true, resetScroll: false })
                    }
                  />
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto pb-36 lg:pb-24" {...scrollerProps('groves')}>
                  {header}
                  <div className="px-4 pt-2 pb-8">
                    <div className="mx-auto max-w-(--page-col)">
                      <KeptGroves />
                    </div>
                  </div>
                </div>
              ),
          }}
        />
        {!(tab === 'groves' && openGrove) && <ScrollFab key={tab} screen="today" />}
      </SidebarInset>
    </SidebarProvider>
  )
}
